import { AddCartItemParams, CartAutoReload } from 'smg/customer-api/carts';
import { DeletePaymentOption, NewPaymentOption } from 'smg/customer-api/paymentoptions';
import * as paypal from 'smg/customer-api/paypal';
import { Session } from 'smg/customer-api/session';
import { UserDetails } from 'smg/customer-api/users';
import { DeleteUserTaxExemption, GetUserTaxExemptions, NewUserTaxExemption, RestoreUserTaxExemption } from 'smg/customer-api/usertaxexemptions';
import { HandleRequestError, IsAxiosError, IsParamErrors } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import { Phone } from 'smg/phone';
import { Load } from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';

import { DateTime } from 'luxon';

import Big from 'big.js';
import axios, { AxiosStatusCode } from 'smg/axios';

import * as address from 'smg/address';
import * as countries from 'smg/countries';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as smgPromo from 'smg/customer-api/promos';
import * as html from 'smg/html';
import * as models from 'smg/models';
import * as template from 'smg/template';
import * as toast from 'smg/toast';

import * as bootstrap from 'bootstrap';

import { DefaultCreditCardDelimiter, formatCreditCard, formatDate, getCreditCardType, registerCursorTracker, unformatCreditCard } from 'cleave-zen';

import { DrawCartItemPreviewFromCart } from '../components/cart-item-preview';
import { Uploader } from '../components/uploader';
import { Relref } from '../hugo';

import { Start, Stop } from 'smg/buttons';
import { G } from 'smg/querystring';
import { FullName } from 'smg/users';
import { FormatAddressOneLine } from '../address';
import * as carts from '../carts';
import * as shippingoptions from '../shippingoptions';
import * as usertaxexemptions from '../usertaxexemptions';

if (document.body.id === 'checkout') {
    void main();
}

interface myWindow extends Window {
    readonly cloudFront: string;
    readonly staxOmniWebPaymentsToken: string;
}

declare const window: myWindow;

interface additionalDetails {
    eventDate?: DateTime;
    marketingPermission: boolean;
    smsOptIn: boolean;
    verifiedDesigns: boolean;
}

interface createOrderResp {
    orderID: string;
    invoiceNumber: number;
    key: string;
}

async function main() {
    interface splitPayment {
        uuid: string;
        paymentOption?: models.PaymentOption;
        paymentOptionTypeID?: models.PaymentOptionTypeID;
        firstName?: string;
        lastName?: string;
        amount: Big;
        formula?: string;
        index?: number;
        billingAddress?: billingAddressForm;
        billingShippingSame?: boolean;
        uploader?: Uploader;
        creditCard?: {
            creditCardNumber: string;
            expireMonth: number;
            expireYear: number;
            securityCode: string;
        };
        bank?: {
            bankAccount: string;
            bankRouting: string;
            accountType: 'checking' | 'savings';
            bankType: 'business' | 'personal';
        };
        purchaseOrder?: {
            purchaseOrderNumber: string;
            purchaseOrderUploadIDs: number[];
        };
        orderPayment?: models.OrderPayment;
        success?: boolean;
        msg?: string;
    }

    const queryQuoteID = G().Get('quote');
    const queryOrderID = G().Get('order');
    const queryQuoteSentInstanceID = G().Get('instance');
    const queryCartID = G().Get('cart');

    if (queryQuoteID) {
        await carts.AddCartItem(
            { quoteID: queryQuoteID },
            {
                promptToCheckout: false,
                hideCartLink: true,
                showToast: false,
                newCart: true,
            },
        ).catch(HandleRequestError);
    } else if (queryOrderID) {
        await carts.AddCartItem(
            { orderID: queryOrderID },
            {
                promptToCheckout: false,
                hideCartLink: true,
                showToast: false,
                newCart: true,
            },
        ).catch(HandleRequestError);
    } else if (queryQuoteSentInstanceID) {
        let q: string;

        if (queryQuoteSentInstanceID.length === 43) { // key
            q = `quoteSentKey:${queryQuoteSentInstanceID}`;
        } else {
            q = `quoteSentInstanceID:${queryQuoteSentInstanceID}`;
        }

        const quotes: AddCartItemParams[] = [];

        await axios.get<hyejoo.Quotes>('/v2/quotes', {
            params: {
                'sort': 'serial desc',
                'max': 100,
                'data': 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key',
                'q': q,
            },
        }).then(resp => {
            const quotesResp = resp.data.quotes;
            for (const quote of quotesResp) {
                quotes.push({ quoteID: quote.key ?? '' });
            }
        }).then(() => carts.AddCartItem(quotes, {
            promptToCheckout: false,
            hideCartLink: true,
            showToast: false,
            newCart: true,
        })).catch(HandleRequestError);
    } else if (queryCartID) {
        await carts.AddCartItem(
            { cartID: queryCartID },
            {
                promptToCheckout: false,
                hideCartLink: true,
                showToast: false,
                newCart: true,
            },
        ).catch(HandleRequestError);
    }

    void CartAutoReload();

    document.body.appendChild(template.Clone('#loading-spinner-template'));
    const $loadingSpinner = document.querySelector('#loading-spinner');
    function startLoading() {
        $loadingSpinner?.classList.remove('d-none');
    }

    function stopLoading() {
        $loadingSpinner?.classList.add('d-none');
    }

    let cartID: string | undefined;

    const userShippingOptions: Map<string, hyejoo.ShippingOption> = new Map<string, hyejoo.ShippingOption>();
    let userTaxExemptions: models.UserTaxExemption[] = [];
    let userPaymentOptions: Set<models.PaymentOption> = new Set<models.PaymentOption>();

    let customer: UserDetails | undefined;
    let shippingOption: models.ShippingOption | undefined;
    let taxExemption: models.UserTaxExemption | undefined;
    let splitPayments: splitPayment[] = [];
    let additionalDetails: additionalDetails = {
        marketingPermission: false,
        smsOptIn: false,
        verifiedDesigns: false,
    };

    let orderResp: createOrderResp | undefined = undefined;

    let promo: smgPromo.PromotionalCode | undefined;

    let promoDiscountAmount: Big = Big(0);
    let subtotalAmount: Big = Big(0);
    let taxAmount: Big = Big(0);
    let shippingAmount: Big = Big(0);
    let totalAmount: Big = Big(0);
    let remainingAmount: Big = Big(0);
    let paidAmount: Big = Big(0);
    let taxRate: Big = Big(0);

    const $spinner = document.querySelector('#checkout-spinner') as HTMLDivElement;

    const $sectionShipping = document.querySelector<HTMLDivElement>('#section-shipping');
    const $sectionTax = document.querySelector<HTMLDivElement>('#section-tax');
    const $sectionPayment = document.querySelector<HTMLDivElement>('#section-payment');
    const $sectionAdditionalDetails = document.querySelector<HTMLDivElement>('#section-additional');
    const $orderSummaryColumn = document.querySelector<HTMLDivElement>('#order-summary-column');

    const $orderSummary = document.querySelector('#order-summary');

    const $savingsAmt = $orderSummary?.querySelector('#savings-amount') as HTMLSpanElement;
    const $shippingAmt = $orderSummary?.querySelector('#shipping-amount') as HTMLSpanElement;
    const $taxAmt = $orderSummary?.querySelector('#tax-amount') as HTMLSpanElement;
    const $subtotalAmt = $orderSummary?.querySelector('#subtotal-amount') as HTMLSpanElement;
    const $totalAmt = $orderSummary?.querySelector('#total-amount') as HTMLSpanElement;
    const $cartTaxRateParents = document.querySelectorAll('.cart-tax-rate-parent');
    const $cartTaxRateEl = document.querySelectorAll('.cart-tax-rate');

    const $savingsLine = $savingsAmt.closest('.pricing-line') as HTMLElement;

    const $promoName = $orderSummary?.querySelector('#promo-code-name') as HTMLSpanElement;
    const $promoEl = $orderSummary?.querySelector('.promo') as HTMLElement;
    const $collapseLink = $promoEl.querySelector<HTMLAnchorElement>('a .link-text') as HTMLAnchorElement;
    const $promoInput = $promoEl.querySelector('#promo') as HTMLInputElement;
    const $promoPreview = $promoEl.querySelector<HTMLElement>('.promo-preview') as HTMLElement;
    const $promoApplyBtn = $promoEl.querySelector<HTMLButtonElement>('.btn') as HTMLButtonElement;

    const $cartItemPreview = document.querySelector('#your-cart-items') as HTMLDivElement;

    const $shippingPreview = $sectionShipping?.querySelector('#shipping-preview') as HTMLDivElement;
    const $shippingPreviewAddress = $shippingPreview.querySelector('#shipping-preview-address') as HTMLElement;
    const $shippingPreviewPhone = $shippingPreview.querySelector('#shipping-preview-phone') as HTMLElement;
    const $shippingPreviewEmail = $shippingPreview.querySelector('#shipping-preview-email') as HTMLElement;

    const $internationalShippingWarning = $sectionShipping?.querySelector<HTMLDivElement>('#intl-shipping-warning') as HTMLDivElement;

    const $shippingBody = $sectionShipping?.querySelector<HTMLDivElement>('.collapse');
    const $shippingBodyToggle = $sectionShipping?.querySelector<HTMLAnchorElement>('#shipping-body-toggle');
    const $shippingOptionsParent = $sectionShipping?.querySelector<HTMLFieldSetElement>('#shipping-options-parent');
    const $shippingButtonsParent = $sectionShipping?.querySelector<HTMLDivElement>('#shipping-buttons-parent');

    const $taxPreview = $sectionTax?.querySelector('#tax-preview') as HTMLDivElement;
    const $taxPreviewTemplate = document.querySelector('#tax-preview-template') as HTMLTemplateElement;

    const $taxStateWarning = $sectionTax?.querySelector<HTMLDivElement>('#tax-state-warning') as HTMLDivElement;

    const $taxBody = $sectionTax?.querySelector<HTMLDivElement>('.collapse');
    const $taxBodyToggle = $sectionTax?.querySelector<HTMLAnchorElement>('#tax-body-toggle');

    const $taxToggleYes = $taxBody?.querySelector<HTMLInputElement>('#tax-exemption-yes') as HTMLInputElement;
    const $taxToggleNo = $taxBody?.querySelector<HTMLInputElement>('#tax-exemption-no') as HTMLInputElement;
    const $taxDetailsParent = $sectionTax?.querySelector<HTMLDivElement>('#tax-exemption-details-parent');
    const $taxConfirmNone = $taxBody?.querySelector<HTMLButtonElement>('#no-tax-exemption-confirm') as HTMLButtonElement;

    const $paymentBodyForm = $sectionPayment?.querySelector<HTMLFormElement>('#payment-body-form');
    const $paymentBodyFieldSet = $paymentBodyForm?.querySelector<HTMLFieldSetElement>('#payment-body-fieldset');
    const $paymentBody = $paymentBodyForm?.querySelector<HTMLDivElement>('.collapse');
    const $paymentBodyToggle = $sectionPayment?.querySelector<HTMLAnchorElement>('#payment-body-toggle');

    const $paymentPreview = $sectionPayment?.querySelector<HTMLDivElement>('#payment-preview') as HTMLDivElement;
    const $paymentPreviewTemplate = document.querySelector<HTMLTemplateElement>('#payment-preview-template') as HTMLTemplateElement;
    const $paymentPreviewItemCC = document.querySelector<HTMLTemplateElement>('#payment-preview-item-cc') as HTMLTemplateElement;
    const $paymentPreviewItemBank = document.querySelector<HTMLTemplateElement>('#payment-preview-item-bank') as HTMLTemplateElement;
    const $paymentPreviewItemOther = document.querySelector<HTMLTemplateElement>('#payment-preview-item-other') as HTMLTemplateElement;

    const $additionalBody = $sectionAdditionalDetails?.querySelector<HTMLDivElement>('.collapse');
    const $additionalBodyToggle = $sectionAdditionalDetails?.querySelector<HTMLAnchorElement>('#additional-body-toggle');

    const $additionalPreview = $sectionAdditionalDetails?.querySelector<HTMLDivElement>('#additional-preview');

    const $authorizePaymentBtn = document.querySelector('#authorize-payment') as HTMLButtonElement;
    const $authorizePaymentText = document.querySelector('#authorize-payment-text') as HTMLElement;
    const $authorizePaymentTextPlaceOrder = document.querySelector('#authorize-payment-text-place-order') as HTMLElement;
    const $authorizePaymentTextPayPal = document.querySelector('#authorize-payment-text-paypal') as HTMLElement;

    const promoInput = new smgPromo.Input($promoInput, {
        success: p => {
            promo = p;
            $savingsLine.classList.remove('d-none');
            $savingsAmt.textContent = DollarFormat(promoDiscountAmount);
            $promoName.textContent = promo.code;
            $promoPreview.innerHTML = `<b>${html.Escape(promo.code)}:</b> ${html.Escape(promo.description)}`;
            $promoPreview.classList.remove('d-none');
            $collapseLink.textContent = 'Change Promo Code';
            void updateOrderSummary();
        },
        error: () => {
            promo = undefined;
            $savingsLine.classList.add('d-none');
            $savingsAmt.textContent = '';
            $promoName.textContent = '';
            $promoPreview.classList.add('d-none');
            $promoPreview.innerHTML = '';
            $collapseLink.textContent = 'Have a Promo Code?';
            void updateOrderSummary();
        },
    });

    $promoApplyBtn.addEventListener('click', async () => {
        promoInput.Dirty = true;
        Start($promoApplyBtn);
        await promoInput.Check().then(() => {
            Stop($promoApplyBtn);
        }).catch(() => {
            Stop($promoApplyBtn);
        });
    });

    // make sure the promo is checked if it's loaded from the url param
    // and run the success/error callbacks
    setTimeout(async () => {
        promoInput.Dirty = true;
        await promoInput.Check();
    });

    updatePromoPlaceholder();
    window.addEventListener('resize', updatePromoPlaceholder);

    const sess = await Session().catch(() => undefined);
    await updateOrderSummary();

    const userDetails = async (): Promise<UserDetails> =>
        await Session().then(sessionResp => ({
            fullName: FullName(sessionResp),
            company: sessionResp.company,
            email: sessionResp.email,
            phone: sessionResp.phone,
            countryID: shippingOption?.countryID,
            stateID: shippingOption?.stateID,
        })).catch(() => ({
            fullName: shippingOption?.fullName || '',
            company: shippingOption?.company || '',
            email: shippingOption?.email || '',
            phone: shippingOption?.phone || '',
            countryID: shippingOption?.countryID,
            stateID: shippingOption?.stateID,
        }));

    await carts.GetCart().then(cartResp => {
        if (!cartResp?.cart) {
            return;
        }
        if (!cartResp?.cart?.cartItems?.length) {
            window.location.href = Relref('/cart');
        }
        DrawCartItemPreviewFromCart($cartItemPreview, cartResp.cart);
    }).catch(err => {
        if (AxiosStatusCode(err) === 404) {
            window.location.href = Relref('/cart');
        } else {
            HandleRequestError(err);
        }
        throw err; // rethrow to prevent further execution
    });

    async function updateTotals() {
        let taxAddr: carts.TaxAddress | undefined;
        if (shippingOption) {
            taxAddr = {
                addressLine1: shippingOption.addressLine1 || '',
                addressLine2: shippingOption.addressLine2,
                city: shippingOption.city || '',
                stateID: shippingOption.stateID || shippingOption.state?.id,
                zip: shippingOption.zip || '',
                countryID: shippingOption.countryID || shippingOption.country?.id || 0,
            };
        }

        if (!carts.GetCartID()) {
            window.location.href = Relref('/cart');
            return;
        }

        await carts.GetCart(taxAddr, promo?.code).catch(err => {
            if (AxiosStatusCode(err) === 404) {
                window.location.href = Relref('/cart');
            } else {
                HandleRequestError(err);
            }
            throw err; // rethrow to prevent further execution
        }).then(cartResp => {
            if (!cartResp) {
                return;
            }

            cartID = cartResp.cart.id;

            promoDiscountAmount = Big(cartResp.discount);
            shippingAmount = Big(cartResp.shipping);
            taxAmount = Big(cartResp.taxAmount);
            subtotalAmount = Big(cartResp.subtotal);
            totalAmount = Big(cartResp.total);
            taxRate = Big(cartResp.taxRate);

            if (taxExemption && ((taxExemption?.stateID && shippingOption?.stateID && taxExemption.stateID === shippingOption.stateID) || typeof taxExemption?.stateID === 'undefined')) {
                taxAmount = Big(0);
                taxRate = Big(0);

                totalAmount = totalAmount.minus(cartResp.taxAmount);
            }
            remainingAmount = totalAmount;

            if (splitPayments.length === 1) {
                splitPayments[0].amount = totalAmount;
            }

            [
                $authorizePaymentText,
                $authorizePaymentTextPlaceOrder,
                $authorizePaymentTextPayPal,
            ].forEach($ => $.classList.add('d-none'));

            let paypal = false;
            let nonPayment = false;
            splitPayments.forEach(sp => {
                remainingAmount = remainingAmount.minus(sp.amount);

                switch (sp.paymentOptionTypeID) {
                case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
                    paypal = true;
                    break;
                case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
                case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
                case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
                    nonPayment = true;
                    break;
                }
            });

            if (paypal) {
                $authorizePaymentTextPayPal.classList.remove('d-none');
            } else if (nonPayment) {
                $authorizePaymentTextPlaceOrder.classList.remove('d-none');
            } else {
                $authorizePaymentText.classList.remove('d-none');
            }
        });
    }

    function updatePromoPlaceholder() {
        const promoInput = document.getElementById('promo') as HTMLInputElement;
        promoInput.placeholder = window.innerWidth > 400 ? 'Enter promo code here...' : 'Promo code...';
    }

    async function updateOrderSummary() {
        await updateTotals().then(() => {
            if (promo) {
                $savingsLine.classList.remove('d-none');
                $savingsAmt.textContent = DollarFormat(promoDiscountAmount);
                $promoName.textContent = promo.code;
            } else {
                $savingsLine.classList.add('d-none');
                $savingsAmt.textContent = '';
                $promoName.textContent = '';
            }
            if (shippingOption) {
                if (shippingAmount.eq(0) && shippingOption.countryID === models.CountryID.COUNTRIES_UNITED_STATES) {
                    $shippingAmt.textContent = 'Free for the USA';
                } else {
                    $shippingAmt.textContent = DollarFormat(shippingAmount);
                }
                if (taxExemption && ((taxExemption?.stateID && shippingOption?.stateID && taxExemption.stateID === shippingOption.stateID) || typeof taxExemption?.stateID === 'undefined')) {
                    $taxAmt.textContent = 'Exempt';
                } else {
                    $cartTaxRateParents.forEach(el => el.classList.remove('d-none'));
                    $cartTaxRateEl.forEach(el => el.textContent = taxRate.mul(100).toFixed(2));
                    $taxAmt.textContent = DollarFormat(taxAmount);
                }
                $subtotalAmt.textContent = DollarFormat(subtotalAmount);
                $totalAmt.textContent = DollarFormat(totalAmount);
            } else {
                $shippingAmt.textContent = 'Calculated at Checkout';
                $taxAmt.textContent = 'Calculated at Checkout';
                $subtotalAmt.textContent = 'Calculated at Checkout';
                $totalAmt.textContent = DollarFormat(totalAmount);
            }
        });
    }

    async function initShipping() {
        startLoading();
        if (sess?.userID) {
            await shippingoptions.Get({
                'data': 'fullName,company,addressLine1,addressLine2,city,stateID,zip,countryID,phone,email,created,lastOrdered',
                'sort': 'lastOrdered desc,created desc',
                'q': 'has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip',
            }).then(shippingOptions => {
                if (shippingOptions.length) {
                    userShippingOptions.clear();
                    shippingOptions.forEach(so => {
                        if (so.id) {
                            userShippingOptions.set(so.id, so);
                        }
                    });
                }
            }).catch(HandleRequestError);
        }

        let shownCount = 0;
        $shippingOptionsParent?.replaceChildren();
        $shippingBody?.querySelector<HTMLElement>('#shipping-option-see-more-parent')?.remove();
        if (userShippingOptions.size) {
            const showMoreUserShippingOptions = async () => {
                const usoArray = Array.from(userShippingOptions.values()).sort((a, b) => {
                    if (a.lastOrdered && b.lastOrdered) {
                        return new Date(b.lastOrdered).valueOf() - new Date(a.lastOrdered).valueOf();
                    }
                    return 0;
                });
                const targetCount = shownCount + 5;
                for (let i = shownCount; i < usoArray.length; i++) {
                    if (i >= targetCount) {
                        break;
                    }
                    const so = usoArray[i];

                    await shippingoptions.
                        Draw(so, template.Clone('#shipping-option-template')).
                        then(drawEl => {
                            if (i === 0) {
                                drawEl.querySelector<HTMLElement>('.tag-container')?.classList.remove('d-none');
                            }
                            $shippingOptionsParent?.appendChild(drawEl);
                        }).catch(HandleRequestError);

                    shownCount++;
                    if (shownCount >= usoArray.length) {
                        $shippingBody?.querySelector<HTMLButtonElement>('#shipping-option-see-more-button')?.remove();
                    }
                }
                $shippingOptionsParent?.querySelectorAll<HTMLElement>('.shipping-option').forEach($so => {
                    // The remove-then-add pattern makes sure we don't add multiple event listeners
                    const $edit = $so.querySelector<HTMLButtonElement>('.shipping-option-edit');
                    $edit?.removeEventListener('click', editShippingOption);
                    $edit?.addEventListener('click', editShippingOption);
                    const $delete = $so.querySelector<HTMLButtonElement>('.shipping-option-delete');
                    $delete?.removeEventListener('click', deleteShippingOption);
                    $delete?.addEventListener('click', deleteShippingOption);
                });
            };
            if ($shippingButtonsParent) {
                $shippingBody?.insertBefore(template.Clone('#shipping-option-see-more'), $internationalShippingWarning);
                $shippingBody?.querySelector<HTMLButtonElement>('#shipping-option-see-more-button')?.addEventListener('click', showMoreUserShippingOptions);
            }
            await showMoreUserShippingOptions();
            $shippingButtonsParent?.replaceChildren(template.Clone('#shipping-option-buttons'));
            $shippingButtonsParent?.querySelector<HTMLButtonElement>('.shipping-option-new')?.addEventListener('click', () => promptShippingOption());
            const $continueBtn = $shippingButtonsParent?.querySelector<HTMLButtonElement>('.shipping-option-continue');
            if ($continueBtn) {
                $continueBtn.addEventListener('click', async () => {
                    $shippingButtonsParent?.querySelectorAll('button').forEach($btn => {
                        $btn.disabled = true;
                    });
                    if ($shippingOptionsParent) {
                        $shippingOptionsParent.disabled = true;
                        const selected = $shippingOptionsParent?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
                        if (selected) {
                            const id = selected.value;
                            const so = userShippingOptions.get(id);
                            if (so) {
                                shippingOption = so;
                                customer = await userDetails();
                                await updateOrderSummary().
                                    then(updateShippingSummary).
                                    then(() => {
                                        if (shippingOption?.countryID === models.CountryID.COUNTRIES_UNITED_STATES) {
                                            $taxBodyToggle?.click();
                                        } else {
                                            $paymentBodyToggle?.click();
                                        }
                                    }).
                                    then(() => {
                                        $shippingOptionsParent.disabled = false;
                                    });
                            }
                        }
                    }
                    $shippingButtonsParent?.querySelectorAll('button').forEach($btn => {
                        $btn.disabled = false;
                    });
                });
            }
        } else {
            promptShippingOption(shippingOption);
        }

        const $deleteConfirmTemplate = document.querySelector('.delete-confirm-template') as HTMLTemplateElement;
        function editShippingOption(e: Event) {
            if (e.target instanceof HTMLButtonElement && e.target.classList.contains('shipping-option-edit')) {
                const $so = e.target.closest('.shipping-option') as HTMLElement;
                const id = $so.querySelector<HTMLInputElement>('input[type="radio"]')?.value || '';
                const so = userShippingOptions.get(id);
                if (so) {
                    promptShippingOption(so);
                }
            }
        }

        const bootstrapTooltips: bootstrap.Tooltip[] = [];
        function deleteShippingOption(e: Event) {
            if (e.target instanceof HTMLButtonElement && e.target.classList.contains('shipping-option-delete')) {
                const $so = e.target.closest('.shipping-option') as HTMLElement;
                const id = $so.querySelector<HTMLInputElement>('input[type="radio"]')?.value || '';

                bootstrapTooltips.forEach(t => t.dispose());
                bootstrapTooltips.length = 0;

                const deleteTooltip = new bootstrap.Tooltip($so, {
                    trigger: 'manual',
                    placement: 'top',
                    title: 'Are you sure you want to delete this image?',
                    html: true,
                    offset: [0, 16],
                });

                bootstrapTooltips.push(deleteTooltip);

                const $deleteConfirm = template.Clone($deleteConfirmTemplate).firstElementChild as HTMLElement;

                const $confirm = $deleteConfirm.querySelector('.delete-confirm-button') as HTMLButtonElement;
                $confirm.addEventListener('click', async () => {
                    await axios.delete(`/shipping-options/${id}`).then(() => {
                        const undoLinkID = `undo-${UUID()}`;
                        const t = toast.Success(`Deleted shipping address. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a>`);

                        const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                        undo.addEventListener('click', async e => {
                            e.preventDefault();
                            t?.dispose();
                            await axios.
                                patch(`/shipping-options/${id}`, {
                                    'active': true,
                                }).
                                then(initShipping);
                        });

                        void initShipping();
                        deleteTooltip.dispose();
                    });
                });

                const $cancel = $deleteConfirm.querySelector('.delete-cancel-button') as HTMLButtonElement;
                $cancel.addEventListener('click', () => {
                    deleteTooltip.dispose();
                    bootstrapTooltips.length = 0;
                });

                deleteTooltip.setContent({ '.tooltip-inner': $deleteConfirm });
                deleteTooltip.show();
            }
        }

        stopLoading();
    }

    await initShipping();

    $shippingBody?.addEventListener('shown.bs.collapse', () => {
        $spinner.classList.add('d-none');
        $sectionShipping?.classList.remove('d-none');
        $sectionTax?.classList.remove('d-none');
        $sectionPayment?.classList.remove('d-none');
        $sectionAdditionalDetails?.classList.remove('d-none');
        $orderSummaryColumn?.classList.remove('d-none');
    });

    $shippingBodyToggle?.click();

    async function updateShippingSummary() {
        return new Promise<void>(resolve => {
            if (shippingOption) {
                return address.Format(shippingOption).then(addr => {
                    $shippingPreviewAddress.innerHTML = html.Nl2br(addr);
                    $shippingPreviewPhone.textContent = shippingOption?.phone || '';
                    $shippingPreviewEmail.textContent = shippingOption?.email || '';
                    const payNum = $sectionPayment?.querySelector<HTMLDivElement>('.number-circle');
                    const addlNum = $sectionAdditionalDetails?.querySelector<HTMLDivElement>('.number-circle');
                    if (shippingOption?.countryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
                        $internationalShippingWarning.classList.remove('d-none');
                        $sectionTax?.classList.add('d-none');
                        if (payNum) {
                            payNum.textContent = '2';
                        }
                        if (addlNum) {
                            addlNum.textContent = '3';
                        }
                    } else {
                        $internationalShippingWarning.classList.add('d-none');
                        $sectionTax?.classList.remove('d-none');
                        if (payNum) {
                            payNum.textContent = '3';
                        }
                        if (addlNum) {
                            addlNum.textContent = '4';
                        }
                    }
                }).then(resolve);
            } else {
                $shippingPreviewAddress.innerHTML = 'Address';
                $shippingPreviewPhone.textContent = 'Telephone';
                $shippingPreviewEmail.textContent = 'Email';
                resolve();
            }
        });
    }

    function promptShippingOption(so?: models.ShippingOption) {
        if (!$shippingBody?.classList.contains('show')) {
            $shippingBodyToggle?.click();
        }
        const form = template.Clone('#new-shipping-option-form');
        if ($shippingOptionsParent) {
            $shippingOptionsParent.replaceChildren(form);
            $shippingOptionsParent.classList.add('show');
            $shippingButtonsParent?.replaceChildren();
            $shippingBody?.querySelector<HTMLElement>('#shipping-option-see-more-parent')?.remove();

            const $shippingFullName = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-full-name') as HTMLInputElement;
            const $shippingCompany = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-company') as HTMLInputElement;
            const $shippingCountry = $shippingOptionsParent.querySelector<HTMLSelectElement>('#new-shipping-option-country') as HTMLSelectElement;
            const $shippingAddressLine1 = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-address-line-1') as HTMLInputElement;
            const $shippingAddressLine2 = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-address-line-2') as HTMLInputElement;
            const $shippingCity = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-city') as HTMLInputElement;
            const $shippingState = $shippingOptionsParent.querySelector<HTMLSelectElement>('#new-shipping-option-state') as HTMLSelectElement;
            const $shippingZipCode = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-zip') as HTMLInputElement;
            const $shippingEmail = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-email') as HTMLInputElement;
            const $shippingPhone = $shippingOptionsParent.querySelector<HTMLInputElement>('#new-shipping-option-phone') as HTMLInputElement;

            const shippingPhoneIti = new Phone($shippingPhone);

            countries.Selects($shippingCountry, $shippingState, {
                defaultUnitedStates: true,
                stateParentSelector: 'div',
                phone: shippingPhoneIti,
                onStateHide: () => $shippingZipCode.parentElement?.classList.replace('col-3', 'col-6'),
                onStateShow: () => $shippingZipCode.parentElement?.classList.replace('col-6', 'col-3'),
            }).then(() => {
                if (so?.countryID && so.countryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
                    $shippingCountry.value = so?.country?.id?.toString() || '';
                    $shippingCountry.dispatchEvent(new Event('change'));
                }
            }).then(() => {
                if (so?.stateID) {
                    $shippingState.value = so.stateID.toString();
                }
            }).catch(HandleRequestError);

            if (so) {
                $shippingFullName.value = so.fullName || '';
                $shippingCompany.value = so.company || '';
                $shippingAddressLine1.value = so.addressLine1 || '';
                $shippingAddressLine2.value = so.addressLine2 || '';
                $shippingCity.value = so.city || '';
                $shippingZipCode.value = so.zip || '';
                $shippingEmail.value = so.email || '';

                void shippingPhoneIti.Iti.promise.then(() => {
                    shippingPhoneIti.Iti.setNumber(so.phone || '');
                });

                const $confirm = $shippingOptionsParent.querySelector<HTMLButtonElement>('#new-shipping-option-confirm') as HTMLButtonElement;
                $confirm.innerText = 'Save';
            }

            if (userShippingOptions.size) {
                const $cancel = $shippingOptionsParent.querySelector<HTMLButtonElement>('#new-shipping-option-cancel') as HTMLButtonElement;
                $cancel.classList.remove('d-none');
                $cancel.addEventListener('click', () => {
                    void initShipping();
                });
            }

            const $form = $shippingOptionsParent.querySelector<HTMLFormElement>('form') as HTMLFormElement;
            const $fieldset = $form.querySelector<HTMLFieldSetElement>('fieldset') as HTMLFieldSetElement;
            $form.noValidate = true;
            $form.addEventListener('submit', async e => {
                e.preventDefault();

                const valid = ReportValidity($form);

                if (valid) {
                    startLoading();
                    $fieldset.disabled = true;
                    let country;
                    let state;
                    if ($shippingCountry) {
                        country = await countries.GetCountry(parseInt($shippingCountry.value, 10));
                        if ($shippingState) {
                            state = await countries.GetState(parseInt($shippingCountry.value, 10), parseInt($shippingState.value, 10));
                        }
                    }
                    const newShippingOption: models.ShippingOption = {
                        userID: sess?.userID,
                        fullName: $shippingFullName?.value,
                        company: $shippingCompany?.value,
                        addressLine1: $shippingAddressLine1?.value,
                        addressLine2: $shippingAddressLine2?.value,
                        city: $shippingCity?.value,
                        zip: $shippingZipCode?.value,
                        phone: shippingPhoneIti.Iti.getNumber(),
                        email: $shippingEmail?.value,
                        countryID: country?.id,
                        country,
                        state,
                    };
                    shippingOption = newShippingOption;
                    customer = await userDetails();
                    if (so?.id) {
                        void axios.delete(`/shipping-options/${so.id}`);
                    }

                    await shippingoptions.
                        New(customer, newShippingOption).
                        then(async resp => {
                            await updateOrderSummary();
                            await updateShippingSummary();
                            shippingOption = resp;
                            if (sess?.userID) {
                                await initShipping();
                            } else {
                                customer = await userDetails();
                                if (shippingOption?.countryID === models.CountryID.COUNTRIES_UNITED_STATES) {
                                    $taxBodyToggle?.click();
                                } else {
                                    $paymentBodyToggle?.click();
                                }
                            }
                        }).
                        catch(e => {
                            HandleRequestError(e);
                            shippingOption = undefined;
                        }).
                        finally(() => {
                            $fieldset.disabled = false;
                            stopLoading();
                        });
                }
            });
        }
    }

    $shippingBodyToggle?.addEventListener('click', () => {
        // give the bootstrap js time to update the class
        setTimeout(() => {
            if (!$shippingBodyToggle?.classList.contains('collapsed')) {
                void initShipping();
                // wait for the collapse transition to finish before scrolling
                setTimeout(() => {
                    $shippingBody?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }, 300);
            }
        }, 50);
    });

    $shippingBody?.addEventListener('click', e => {
        if (e.target instanceof HTMLInputElement && e.target.classList.contains('shipping-option-input')) {
            const id = e.target.value;
            const so = userShippingOptions.get(id);
            if (so) {
                shippingOption = so;
                void updateOrderSummary();
                void updateShippingSummary();
            }
        }
    });

    const checkTaxSelected = () => {
        if ($taxToggleYes.checked) {
            $taxDetailsParent?.classList.remove('d-none');
            $taxConfirmNone.parentElement?.classList.add('d-none');
        } else {
            $taxDetailsParent?.classList.add('d-none');
            $taxConfirmNone.parentElement?.classList.remove('d-none');
        }
    };

    let taxFirstOpen = true;
    async function initTax() {
        $taxBodyToggle?.classList.add('d-none');
        checkTaxSelected();
        startLoading();
        if (sess?.userID) {
            await updateTaxExemptionOptions(sess.userID).then(async () => {
                $taxDetailsParent?.replaceChildren();
                if (userTaxExemptions.length) {
                    const drawPromises: Promise<void>[] = [];
                    userTaxExemptions.forEach(ute => {
                        drawPromises.push(
                            new Promise<void>(resolve => {
                                usertaxexemptions.
                                    Draw(ute).
                                    then(drawEl => {
                                        $taxDetailsParent?.appendChild(drawEl);
                                        resolve();
                                    }).
                                    catch(HandleRequestError);
                            }),
                        );
                    });
                    await Promise.all(drawPromises).
                        then(() => {
                            $taxDetailsParent?.appendChild(template.Clone('#tax-exemption-buttons'));
                            $taxDetailsParent?.querySelector<HTMLButtonElement>('.tax-exemption-new')?.addEventListener('click', () => {
                                promptTaxExemption();
                            });
                            $taxDetailsParent?.querySelector<HTMLButtonElement>('.tax-exemption-continue')?.addEventListener('click', async () => {
                                const selected = $taxDetailsParent?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
                                if (selected) {
                                    const id = selected.value;
                                    const ute = userTaxExemptions.find(u => u.id === id);
                                    if (ute) {
                                        taxExemption = ute;
                                        customer = await userDetails();
                                        void updateOrderSummary();
                                        void updateShippingSummary();
                                        void updateTaxExemptionSummary();
                                    }
                                    $taxBodyToggle?.classList.remove('d-none');
                                    $paymentBodyToggle?.click();
                                }
                            });
                            $taxDetailsParent?.querySelectorAll<HTMLElement>('.user-tax-exemption').forEach($ute => {
                                const id = $ute.querySelector<HTMLInputElement>('input[type="radio"]')?.value || '';
                                const $edit = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-edit');
                                if ($edit && id !== '') {
                                    $edit.addEventListener('click', () => {
                                        const ute = userTaxExemptions.find(u => u.id === id);
                                        if (ute) {
                                            promptTaxExemption(ute);
                                        }
                                    });
                                }
                                const $delete = $ute.querySelector<HTMLButtonElement>('.user-tax-exemption-delete');
                                const $deleteConfirmTemplate = document.querySelector('.delete-confirm-template') as HTMLTemplateElement;
                                if ($delete && id !== '') {
                                    $delete.addEventListener('click', () => {
                                        const deleteTooltip = new bootstrap.Tooltip($ute, {
                                            trigger: 'manual',
                                            placement: 'top',
                                            title: 'Are you sure you want to delete this tax exemption?',
                                            html: true,
                                            offset: [0, 16],
                                        });

                                        const $deleteConfirm = template.Clone($deleteConfirmTemplate).firstElementChild as HTMLElement;

                                        const $confirm = $deleteConfirm.querySelector('.delete-confirm-button') as HTMLButtonElement;
                                        $confirm.addEventListener('click', async () => {
                                            await DeleteUserTaxExemption(id).then(() => {
                                                const undoLinkID = `undo-${UUID()}`;
                                                const t = toast.Success(`Deleted tax exemption. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a>`);

                                                const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                                                undo.addEventListener('click', async e => {
                                                    e.preventDefault();
                                                    t?.dispose();
                                                    await RestoreUserTaxExemption(id).
                                                        then(initTax).
                                                        then(() => {
                                                            $taxToggleYes.click();
                                                        });
                                                });

                                                void initTax();
                                                deleteTooltip.dispose();
                                            });
                                        });

                                        const $cancel = $deleteConfirm.querySelector('.delete-cancel-button') as HTMLButtonElement;
                                        $cancel.addEventListener('click', () => deleteTooltip.dispose());

                                        const msg = $deleteConfirm.querySelector('.delete-confirm-message') as HTMLDivElement;
                                        msg.textContent = 'Are you sure you want to delete this tax exemption?';
                                        deleteTooltip.setContent({ '.tooltip-inner': $deleteConfirm });
                                        deleteTooltip.show();
                                    });
                                }
                            });
                            let firstTaxExemption = $taxDetailsParent?.querySelector<HTMLInputElement>('.address:first-child input[type="radio"]');
                            if (taxExemption?.id) {
                                firstTaxExemption = $taxDetailsParent?.querySelector<HTMLInputElement>(`input[value="${taxExemption.id}"]`);
                            }
                            if (firstTaxExemption) {
                                firstTaxExemption.checked = true;
                            }
                        }).
                        then(() => {
                            if (taxFirstOpen) {
                                if (taxExemption) {
                                    $taxToggleYes.click();
                                } else {
                                    $taxToggleNo.click();
                                }
                            }
                            taxFirstOpen = false;
                        });
                } else {
                    promptTaxExemption();
                }
            });
        } else {
            promptTaxExemption(taxExemption);
        }
        stopLoading();
    }

    if ($taxBody && $taxDetailsParent) {
        [$taxToggleYes, $taxToggleNo].forEach($toggle => {
            $toggle.addEventListener('change', checkTaxSelected);
        });
    }

    $taxConfirmNone.addEventListener('click', () => {
        taxExemption = undefined;
        void updateOrderSummary();
        void updateShippingSummary();
        void updateTaxExemptionSummary();
        $taxBodyToggle?.classList.remove('d-none');
        $paymentBodyToggle?.click();
    });

    async function updateTaxExemptionOptions(userID: string) {
        if (userID === '') {
            return;
        }
        userTaxExemptions = [];
        const getUserTaxExemption = async (id?: string): Promise<models.UserTaxExemption[]> =>
            GetUserTaxExemptions({
                'data': 'number,countryID,stateID,userTaxExemptionUploads,userTaxExemptionReasonID,userTaxExemptionReason',
                'sort': 'lastOrdered desc,created desc',
                'size': '1',
                'q': id ? `id:${id}` : '',
            }).then(userTaxExemptions => {
                if (id) {
                    if (userTaxExemptions.length) {
                        return userTaxExemptions;
                    }
                    return getUserTaxExemption();
                }
                return userTaxExemptions;
            });
        await getUserTaxExemption().then(resp => {
            userTaxExemptions = resp;
            userTaxExemptions.sort((a, b) => new Date(b.updated ?? '').valueOf() - new Date(a.updated ?? '').valueOf());
        });
    }

    async function updateTaxExemptionSummary() {
        $taxPreview.classList.value = 'preview';
        if (taxExemption) {
            $taxPreview.classList.value = 'preview px-md-17 pt-2 pb-10 d-flex flex-column flex-md-row';
            $taxPreview?.replaceChildren(template.Clone($taxPreviewTemplate));
            const $type = $taxPreview?.querySelector<HTMLSpanElement>('.tax-preview-type') as HTMLSpanElement;
            const $number = $taxPreview?.querySelector<HTMLSpanElement>('.tax-preview-number') as HTMLSpanElement;
            const $state = $taxPreview?.querySelector<HTMLSpanElement>('.tax-preview-state') as HTMLSpanElement;

            $type.textContent = taxExemption.userTaxExemptionReason?.name || '';
            $number.textContent = taxExemption.number || '';

            const statecode = await countries.GetState(taxExemption.countryID || 0, taxExemption.stateID || 0).then(state => state?.stateCode || 'US');

            $state.textContent = statecode;

            const $attachmentsContainer = $taxPreview?.querySelector<HTMLDivElement>('.attachments-container') as HTMLDivElement;

            taxExemption.userTaxExemptionUploads?.forEach(async uteu => {
                const $img = document.createElement('img');
                let $attachment: HTMLElement | undefined;
                await Load($img, `${window.cloudFront ?? ''}/${uteu.upload?.key ?? ''}`, uteu.upload?.name ?? '').
                    then(success => {
                        if (success) {
                            $attachment = template.Clone('#upload-image-placeholder');
                            $attachment.querySelector<HTMLElement>('.upload')?.insertBefore($img, $attachment.querySelector('.upload')?.firstChild || null);
                        } else {
                            $attachment = template.Clone('#upload-document-placeholder');
                        }
                        return $attachment;
                    }).
                    then($attachment => {
                        if ($attachment) {
                            ($attachment.querySelector<HTMLDivElement>('.document-name') as HTMLDivElement).textContent = uteu.upload?.name || `Attachment ${taxExemption?.userTaxExemptionUploads?.indexOf(uteu).toString() || ''}`;
                            $attachmentsContainer.appendChild($attachment);
                        }
                    });
            });
        } else {
            $taxPreview?.replaceChildren(html.FromString('<div class="w-100 py-16 px-md-20 text-body-secondary text-center fs-4 fst-italic">No tax exemption selected</div>'));
        }
    }

    function promptTaxExemption(ute?: models.UserTaxExemption) {
        const taxTemplate = template.Clone('#new-tax-exemption-form');
        if ($taxDetailsParent) {
            $taxDetailsParent.replaceChildren(taxTemplate);
            $taxDetailsParent.classList.add('show');

            const $form = $taxDetailsParent.querySelector<HTMLFormElement>('form') as HTMLFormElement;

            const $details = $taxDetailsParent.querySelector<HTMLDivElement>('#tax-exemption-details') as HTMLDivElement;
            const $type = $details.querySelector<HTMLSelectElement>('#tax-exemption-type') as HTMLSelectElement;
            const $number = $details.querySelector<HTMLInputElement>('#tax-exemption-number') as HTMLInputElement;
            const $state = $details.querySelector<HTMLSelectElement>('#tax-exemption-state') as HTMLSelectElement;

            const $taxUploaderInput = $details.querySelector<HTMLInputElement>('#tax-exemption-uploader') as HTMLInputElement;
            const $taxUploaderLabel = $taxUploaderInput.closest('.uploader-dropzone') as HTMLLabelElement;

            let taxUploads: number[] = [];

            const $taxConfirm = $taxDetailsParent.querySelector<HTMLButtonElement>('#new-tax-exemption-confirm') as HTMLButtonElement;
            const $taxCancel = $taxDetailsParent.querySelector<HTMLButtonElement>('#new-tax-exemption-cancel') as HTMLButtonElement;

            countries.
                StateSelect(models.CountryID.COUNTRIES_UNITED_STATES, $state, {
                    defaultUnitedStates: true,
                    shortStates: true,
                }).
                then(() => {
                    $state.addEventListener('change', () => {
                        if ($state.value !== 'all' && shippingOption?.stateID && $state.value !== shippingOption.stateID.toString()) {
                            $taxStateWarning.classList.remove('d-none');
                        } else {
                            $taxStateWarning.classList.add('d-none');
                        }
                    });
                    if (ute?.stateID) {
                        $state.value = ute.stateID.toString();
                    } else if (shippingOption?.stateID) {
                        $state.value = shippingOption.stateID.toString();
                    }
                }).
                catch(HandleRequestError);

            if (ute) {
                $taxToggleYes.click();
                $type.value = ute.userTaxExemptionReasonID?.toString() || '';
                $number.value = ute.number || '';
                $state.value = ute.stateID?.toString() || '';
                taxUploads = ute.userTaxExemptionUploads?.map(u => u.uploadID).filter(id => id !== undefined) || [];

                $taxConfirm.innerText = 'Save';
            }

            const taxUploader = new Uploader($taxUploaderLabel, {
                uploadIDs: taxUploads,
                confirmBtn: $taxConfirm,
            });

            if (taxUploader.GetUploadIDs().length) {
                $taxUploaderInput.setCustomValidity('Please upload a file');
            }

            $taxUploaderLabel.classList.value = 'uploader-dropzone validity-parent';
            $taxDetailsParent.querySelector('.uploader-thumbnails')?.classList.add('mt-6');

            if (userTaxExemptions.length) {
                $taxCancel.classList.remove('d-none');
                $taxCancel.addEventListener('click', async () => {
                    await initTax().then(() => {
                        $taxToggleYes.click();
                    });
                });
            }

            $form.noValidate = true;
            $taxConfirm.addEventListener('click', async e => {
                e.preventDefault();
                $taxUploaderInput.setCustomValidity('');
                if (!taxUploader.GetUploadIDs().length) {
                    $taxUploaderInput.setCustomValidity('Please upload a file');
                }
                const valid = ReportValidity($form);
                if (valid) {
                    if ($type.value && $number.value && customer) {
                        startLoading();
                        $taxConfirm.disabled = true;
                        if (ute?.id) {
                            await DeleteUserTaxExemption(ute.id);
                        }
                        await NewUserTaxExemption({
                            user: customer,
                            userTaxExemptReasonID: parseInt($type.value, 10),
                            number: $number.value,
                            countryID: models.CountryID.COUNTRIES_UNITED_STATES,
                            stateID: $state.value !== 'all' ? parseInt($state.value, 10) : undefined,
                            uploadIDs: taxUploader.GetUploadIDs(),
                        }).
                            then(ute => {
                                taxExemption = ute;
                                toast.Success(`Your tax exemption has been added${sess?.userID ? ' to your account' : ''}.`);
                                if (ute.stateID && shippingOption?.stateID && ute.stateID !== shippingOption.stateID) {
                                    $taxStateWarning.classList.remove('d-none');
                                } else {
                                    $taxStateWarning.classList.add('d-none');
                                }
                            }).
                            then(() => $paymentBodyToggle?.click()).
                            then(updateOrderSummary).
                            then(updateTaxExemptionSummary).
                            catch(HandleRequestError).
                            finally(() => {
                                $taxConfirm.disabled = false;
                                stopLoading();
                            });
                    }
                }
            });
        }
    }

    $taxBodyToggle?.addEventListener('click', () => {
        if (taxExemption) {
            $taxDetailsParent?.classList.remove('d-none');
        } else {
            $taxDetailsParent?.classList.add('d-none');
        }
        // setTimeout gives the bootstrap js time to update the class
        setTimeout(async () => {
            if (!$taxBodyToggle?.classList.contains('collapsed')) {
                await initTax();
                // wait for the collapse transition to finish before scrolling
                setTimeout(() => {
                    $taxBody?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }, 300);
            }
        }, 50);
    });

    $taxDetailsParent?.addEventListener('click', e => {
        if (e.target instanceof HTMLInputElement && e.target.classList.contains('user-tax-exemption-input')) {
            const id = e.target.value;
            const ute = userTaxExemptions.find(u => u.id === id);
            if (ute) {
                taxExemption = ute;
                void updateOrderSummary();
                void updateTaxExemptionSummary();
                if (ute.stateID && shippingOption?.stateID && ute.stateID !== shippingOption.stateID) {
                    $taxStateWarning.classList.remove('d-none');
                } else {
                    $taxStateWarning.classList.add('d-none');
                }
            }
        }
    });

    async function initPayment() {
        $paymentBodyToggle?.classList.add('d-none');
        startLoading();
        userPaymentOptions = new Set();
        updatePaymentSummary();
        if (splitPayments.length === 0) {
            splitPayments.push({
                uuid: UUID(),
                amount: Big(0),
                billingShippingSame: true,
            });
        }
        if ($paymentBodyFieldSet) {
            $paymentBodyFieldSet.disabled = false;
        }
        $paymentBody?.replaceChildren();
        const paymentPromises: Promise<HTMLDivElement>[] = [];
        splitPayments.forEach((sp, i) => {
            paymentPromises.push(new Promise(resolve => {
                let paymentOptionsPage = 0;
                const $splitPaymentElement = document.createElement('div');
                if (splitPayments.length > 1) {
                    $splitPaymentElement.classList.add('split-payment');
                    $splitPaymentElement.id = `split-payment-${sp.uuid}`;
                }
                $splitPaymentElement.classList.add('split-payment-inner');
                $splitPaymentElement.setAttribute('data-sp-index', i.toString());
                if (sp.paymentOptionTypeID && !sp.paymentOption) {
                    promptExistingPaymentOption(sp, $splitPaymentElement);
                    resolve($splitPaymentElement);
                } else {
                    const addMorePaymentOptionCards = async () => {
                        let paymentOptionsPromise: Promise<models.PaymentOption[]>;
                        let nextPage: boolean = false;
                        if (sess?.userID) {
                            paymentOptionsPromise = getPaymentOptions(paymentOptionsPage).
                                then(resp => {
                                    nextPage = resp.nextPage;
                                    return resp.paymentOptions;
                                });
                        } else {
                            paymentOptionsPromise = new Promise<models.PaymentOption[]>(resolve => {
                                const full = splitPayments.
                                    map(sp => sp.paymentOption).
                                    filter(po => po !== undefined).
                                    filter(po =>
                                        po?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK ||
                                        po?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT,
                                    );
                                const out = full.slice(5 * paymentOptionsPage, (5 * paymentOptionsPage) + 5);
                                if (full.length > (5 * paymentOptionsPage) + 5) {
                                    nextPage = true;
                                } else {
                                    nextPage = false;
                                }
                                resolve(out);
                            });
                        }
                        await paymentOptionsPromise.then(paymentOptions => {
                            if (paymentOptions.length) {
                                paymentOptions.forEach(async upo => {
                                    userPaymentOptions.add(upo);
                                    if (upo.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
                                        const $el = template.Clone('#payment-option-card-bank');
                                        const $input = $el.querySelector<HTMLInputElement>('input');
                                        const $label = $el.querySelector<HTMLLabelElement>('label');
                                        if ($input && $label && upo.id) {
                                            $input.value = upo.id;
                                            $input.id = `${sp.uuid}-${upo.id}`;
                                            $input.name = sp.uuid;
                                            $label.htmlFor = `${sp.uuid}-${upo.id}`;
                                        }
                                        const bankType = $el.querySelector('.bank-acct-type') as HTMLElement;
                                        if (upo.bankType && upo.accountType) {
                                            bankType.textContent = `${capitalizeFirstLetter(upo.bankType)} ${capitalizeFirstLetter(upo.accountType)} Account`;
                                            if (upo.creditCardLast4) {
                                                bankType.textContent += ` (...${upo.creditCardLast4})`;
                                            }
                                        }
                                        const poAddress: models.ShippingOption = {
                                            city: upo.city,
                                            state: await countries.GetState(upo.country?.id ?? upo.countryID, upo.state?.id ?? upo.stateID),
                                            zip: upo.zip,
                                            country: await countries.GetCountry(upo.country?.id ?? upo.countryID),
                                        };
                                        const $address = $el.querySelector('.bank-address') as HTMLElement;
                                        $address.innerHTML = await FormatAddressOneLine(poAddress);
                                        $splitPaymentElement.appendChild($el);
                                    } else if (upo.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
                                        const $el = template.Clone('#payment-option-card-cc');
                                        const $input = $el.querySelector<HTMLInputElement>('input');
                                        const $label = $el.querySelector<HTMLLabelElement>('label');
                                        if ($input && $label && upo.id) {
                                            $input.value = upo.id;
                                            $input.id = `${sp.uuid}-${upo.id}`;
                                            $input.name = sp.uuid;
                                            $label.htmlFor = `${sp.uuid}-${upo.id}`;
                                        }
                                        const $ccLastFour = $el.querySelector('.cc-last-four') as HTMLElement;
                                        const $ccIssuer = $el.querySelector('.cc-issuer') as HTMLElement;
                                        const poAddress: models.ShippingOption = {
                                            city: upo.city,
                                            state: await countries.GetState(upo.country?.id ?? upo.countryID, upo.state?.id ?? upo.stateID),
                                            zip: upo.zip,
                                            country: await countries.GetCountry(upo.country?.id ?? upo.countryID),
                                        };
                                        const $address = $el.querySelector('.cc-address') as HTMLElement;
                                        $address.innerHTML = await FormatAddressOneLine(poAddress);
                                        if (upo.creditCardLast4) {
                                            $ccLastFour.textContent = upo.creditCardLast4.toString().padStart(4, '0');
                                        }
                                        if (upo.creditCardFirst6) {
                                            switch (getCreditCardType(upo.creditCardFirst6.toString()).toString()) {
                                            case 'amex':
                                                $ccIssuer.textContent = 'Amex';
                                                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                                                $el.querySelector('.cc-icon-amex')?.classList.remove('d-none');
                                                break;
                                            case 'discover':
                                                $ccIssuer.textContent = 'Discover';
                                                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                                                $el.querySelector('.cc-icon-discover')?.classList.remove('d-none');
                                                break;
                                            case 'mastercard':
                                                $ccIssuer.textContent = 'Mastercard';
                                                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                                                $el.querySelector('.cc-icon-mastercard')?.classList.remove('d-none');
                                                break;
                                            case 'visa':
                                                $ccIssuer.textContent = 'Visa';
                                                $el.querySelector('.cc-icon-generic')?.classList.add('d-none');
                                                $el.querySelector('.cc-icon-visa')?.classList.remove('d-none');
                                                break;
                                            }
                                        }
                                        $splitPaymentElement.appendChild($el);
                                    }
                                });
                                if (paymentOptionsPage === 0 && nextPage) {
                                    const $seeMoreBtn = template.Clone('#payment-option-see-more');
                                    $seeMoreBtn.querySelector('button')?.addEventListener('click', async () => {
                                        paymentOptionsPage++;
                                        await addMorePaymentOptionCards();
                                    });
                                    setTimeout(() => {
                                        $splitPaymentElement?.appendChild($seeMoreBtn);
                                    });
                                }
                                if (!nextPage) {
                                    $splitPaymentElement?.querySelector('#payment-option-see-more-parent')?.remove();
                                }
                                if (splitPayments.length > 1 && !$splitPaymentElement.querySelector<HTMLElement>('#payment-new')) {
                                    setTimeout(() => initSplitPaymentButtons($splitPaymentElement));
                                }
                                $splitPaymentElement?.querySelector<HTMLButtonElement>('#payment-new')?.addEventListener('click', () => promptNewPaymentOption($splitPaymentElement));
                            } else {
                                promptNewPaymentOption($splitPaymentElement);
                            }
                        });
                        return $splitPaymentElement;
                    };
                    resolve(addMorePaymentOptionCards().then(() => $splitPaymentElement));
                    // } else {
                    //     promptNewPaymentOption($splitPaymentElement);
                    //     resolve($splitPaymentElement);
                    // }
                }
            }));
        });
        await Promise.all(paymentPromises).then($paymentElements => {
            $paymentElements.forEach($pe => {
                const indexAttr = $pe.getAttribute('data-sp-index');
                $paymentBody?.appendChild($pe);
                if (indexAttr) {
                    // This setTimeout gives all the elements a chance to be loaded in. Without it, none of the
                    // event listeners for the edit or delete buttons are attached.
                    setTimeout(() => {
                        const index = parseInt(indexAttr, 10);
                        const $sp = $paymentBody?.querySelector<HTMLDivElement>(`div[data-sp-index="${index}"]`);
                        if (!$sp) {
                            return;
                        }
                        const sp = splitPayments[index];
                        if (splitPayments.length > 1) {
                            $sp.querySelector<HTMLElement>('.payment-options-nav-new-po')?.remove();

                            if (!$sp.querySelector<HTMLElement>('.split-payment-amount')) {
                                initAmountField($sp, index);
                            }
                            const $header = $sp.querySelector('h4');
                            if (!$header) {
                                const $splitPaymentHeader = document.createElement('div');
                                $splitPaymentHeader.classList.add('split-payment-header', 'd-flex', 'align-items-center', 'flex-column', 'flex-sm-row');

                                const $splitPaymentHeaderText = document.createElement('h4');
                                $splitPaymentHeaderText.classList.add('fw-bold', 'text-primary', 'm-0');
                                $splitPaymentHeaderText.textContent = `Payment Option ${index + 1}`;
                                $splitPaymentHeader.appendChild($splitPaymentHeaderText);

                                if (index !== 0) {
                                    const $splitPaymentHeaderRemoveBtn = document.createElement('button');
                                    $splitPaymentHeaderRemoveBtn.classList.add('btn', 'btn-link', 'ms-auto');
                                    $splitPaymentHeaderRemoveBtn.innerHTML = '<span class="material-symbols-rounded">delete</span> Remove';
                                    $splitPaymentHeaderRemoveBtn.addEventListener('click', () => {
                                        splitPayments.splice(index, 1);
                                        void initPayment();
                                    });
                                    $splitPaymentHeader.appendChild($splitPaymentHeaderRemoveBtn);
                                }

                                $sp?.insertBefore($splitPaymentHeader, $sp?.firstElementChild);
                            }
                        }
                        $sp.querySelectorAll<HTMLButtonElement>('.payment-option-edit').forEach($editBtn => {
                            const $inputParent = $editBtn.closest('.card-body:has(input)');
                            const $input = $inputParent?.querySelector<HTMLInputElement>('input');
                            const id = $input?.value;
                            if (id) {
                                $editBtn.addEventListener('click', () => {
                                    const upo = Array.from(userPaymentOptions).find(u => u.id === id);
                                    if (upo) {
                                        editSavedPaymentOption(upo.id ?? '');
                                    }
                                });
                            }
                        });
                        $sp.querySelectorAll<HTMLButtonElement>('.payment-option-delete').forEach($deleteBtn => {
                            const $inputParent = $deleteBtn.closest('.card-body:has(input)');
                            const $input = $inputParent?.querySelector<HTMLInputElement>('input');
                            const id = $input?.value;
                            if (id) {
                                $deleteBtn.addEventListener('click', async () => {
                                    const upo = Array.from(userPaymentOptions).find(u => u.id === id);
                                    if (upo) {
                                        await deleteSavedPaymentOption(upo.id ?? '');
                                    }
                                });
                            }
                        });

                        switch (sp.paymentOption?.paymentOptionTypeID) {
                        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
                        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
                            setTimeout(() => {
                                const $select = $sp.querySelector<HTMLInputElement>(`input[value="${sp.paymentOption?.id || ''}"]`);
                                if ($select) {
                                    $select.checked = true;
                                }
                            });
                            break;
                        }
                        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO: {
                            setTimeout(() => {
                                const $poNumberInput = $sp.querySelector<HTMLInputElement>('.po-num');
                                if ($poNumberInput) {
                                    $poNumberInput.value = sp.purchaseOrder?.purchaseOrderNumber || '';
                                }
                            });
                            break;
                        }
                        }
                    });
                }
            });
            if (!$paymentBody?.querySelector('.payment-options-nav')) {
                if (splitPayments.length > 1) {
                    $paymentBody?.appendChild(template.Clone('#payment-options-split-nav-template'));
                } else {
                    // don't show the nav if there is only one split payment, and it is not a saved payment option (i.e. in progress). Use the buttons from the template instead.
                    if (!($paymentBody?.querySelector('.new-payment-option') || $paymentBody?.querySelector('.new-payment-option-select'))) {
                        $paymentBody?.appendChild(template.Clone('#payment-options-nav-template'));
                        $paymentBody?.querySelector<HTMLButtonElement>('#payment-new')?.addEventListener('click', () => promptNewPaymentOption($paymentBody.firstElementChild ? $paymentBody.firstElementChild as HTMLElement : undefined));
                        $paymentBody?.querySelector<HTMLButtonElement>('#payment-option-confirm')?.addEventListener('click', async () => {
                            if ($paymentBodyForm && !ReportValidity($paymentBodyForm)) {
                                return;
                            }
                            $paymentBody.querySelectorAll<HTMLInputElement>('input[type=radio]').forEach($input => $input.dispatchEvent(new Event('clearinvalid')));
                            const $selected = $paymentBody?.querySelector<HTMLInputElement>('.payment-option input[type="radio"]:checked');
                            const id = $selected?.value;
                            if (id) {
                                if ($paymentBodyFieldSet) {
                                    $paymentBodyFieldSet.disabled = true;
                                }
                                startLoading();
                                const upo = Array.from(userPaymentOptions).find(u => u.id === id);
                                if (upo) {
                                    const index = 0;
                                    // if (splitPayments.length > 1) {
                                    //     index = parseInt($paymentBody.parentElement?.getAttribute('data-sp-index') || '0', 10);
                                    // }
                                    splitPayments[index].paymentOption = upo;
                                    splitPayments[index].paymentOptionTypeID = upo.paymentOptionTypeID;
                                    if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                                        splitPayments[index].purchaseOrder = undefined;
                                    }
                                    if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
                                        splitPayments[index].bank = undefined;
                                    }
                                    if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
                                        splitPayments[index].creditCard = undefined;
                                    }
                                }
                                if ($paymentBodyFieldSet) {
                                    $paymentBodyFieldSet.disabled = false;
                                }
                                updatePaymentSummary();
                                await updateTotals().
                                    then(updatePaymentSummary).
                                    then(updateOrderSummary).
                                    catch(HandleRequestError).
                                    finally(() => {
                                        stopLoading();
                                        $paymentBodyToggle?.classList.remove('d-none');
                                        $additionalBodyToggle?.click();
                                    });
                            } else {
                                return;
                            }
                        });
                    }
                }
                // $paymentBody?.querySelector<HTMLButtonElement>('#payment-split')?.addEventListener('click', () => {
                //     if (splitPayments.length <= 1) {
                //         splitPayments[0].amount = Big(0);
                //     }
                //     splitPayments.push({
                //         uuid: UUID(),
                //         amount: Big(0),
                //         billingShippingSame: true,
                //     });
                //     void initPayment();
                // });
            }
            $paymentBody?.querySelectorAll<HTMLDivElement>('.split-payment-inner').forEach($sp => {
                $sp.addEventListener('click', () => {
                    $sp.querySelectorAll<HTMLInputElement>('input[type=radio]').forEach($input => $input.dispatchEvent(new Event('clearinvalid')));
                    const $selected = $sp?.querySelector<HTMLInputElement>('.payment-option input[type="radio"]:checked');
                    const id = $selected?.value;
                    if (id) {
                        const upo = Array.from(userPaymentOptions).find(u => u.id === id);
                        if (upo) {
                            let index = 0;
                            if (splitPayments.length > 1) {
                                index = parseInt($sp.parentElement?.getAttribute('data-sp-index') || '0', 10);
                            }
                            splitPayments[index].paymentOption = upo;
                            splitPayments[index].paymentOptionTypeID = upo.paymentOptionTypeID;
                            if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                                splitPayments[index].purchaseOrder = undefined;
                            }
                            if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
                                splitPayments[index].bank = undefined;
                            }
                            if (upo.paymentOptionTypeID !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
                                splitPayments[index].creditCard = undefined;
                            }

                            [
                                $authorizePaymentText,
                                $authorizePaymentTextPlaceOrder,
                                $authorizePaymentTextPayPal,
                            ].forEach($ => $.classList.add('d-none'));

                            let paypal = false;
                            let nonPayment = false;
                            switch (upo.paymentOptionTypeID) {
                            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
                                paypal = true;
                                break;
                            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
                            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
                            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
                                nonPayment = true;
                                break;
                            }

                            if (paypal) {
                                $authorizePaymentTextPayPal.classList.remove('d-none');
                            } else if (nonPayment) {
                                $authorizePaymentTextPlaceOrder.classList.remove('d-none');
                            } else {
                                $authorizePaymentText.classList.remove('d-none');
                            }
                        }
                    }
                });
            });
            if (splitPayments.length > 1) {
                updateSplitPaymentAmountsRemaining();
            }
        }).finally(() => {
            stopLoading();
        });
    }

    $paymentBodyToggle?.addEventListener('click', () => {
        // setTimeout gives the bootstrap js time to update the class
        setTimeout(() => {
            if (!$paymentBodyToggle?.classList.contains('collapsed')) {
                void initPayment();
                // wait for the collapse transition to finish before scrolling
                setTimeout(() => {
                    $paymentBody?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }, 300);
            }
        });
        updatePaymentSummary();
        void updateTotals();
        void updateOrderSummary();
    });

    const updateSplitPaymentAmountsRemaining = () => {
        let amountRemaining = Big(totalAmount);
        splitPayments.forEach(sp => amountRemaining = amountRemaining.minus(sp.amount));
        $paymentBody?.querySelectorAll<HTMLElement>('.remainder .remaining-amount').forEach($input => $input.textContent = DollarFormat(amountRemaining));
        const $continue = $paymentBody?.querySelector<HTMLButtonElement>('#payment-option-confirm');
        if ($continue) {
            if (amountRemaining.lte(0)) {
                $continue.disabled = false;
            } else {
                $continue.disabled = true;
            }
        }
    };

    async function getPaymentOptions(page?: number): Promise<hyejoo.PaymentOptions> {
        const params = {
            'q': `valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip paymentOptionTypeID:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT},${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK}`,
            'data': 'fullName,company,addressLine1,addressLine2,city,stateID,zip,countryID,phone,email,creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID,bankType,accountType,lastOrdered',
            'sort': 'lastOrdered desc,created desc',
            'p': page?.toString() || '0',
            'max': '5',
        };
        return axios.get<hyejoo.PaymentOptions>('/payment-options', { params }).then(resp => resp.data);
    }

    function initAmountField($el: HTMLElement, i: number) {
        const sp = splitPayments[i];
        if ($el.querySelector('.split-payment-amount-input')) {
            $el.querySelector('.split-payment-amount')?.remove();
        }
        const $amountInputClone = template.Clone('#payment-options-split-amount');
        const $splitPaymentAmountInput = $amountInputClone?.querySelector<HTMLInputElement>('.split-payment-amount-input');
        const $splitPaymentRemainingAmount = $amountInputClone?.querySelector('.remaining-amount');
        if ($splitPaymentAmountInput) {
            $splitPaymentAmountInput.addEventListener('input', () => {
                const val = $splitPaymentAmountInput.value.replace(/[^0-9]/g, '');
                if (val) {
                    const bigVal = Big(val).div(100);
                    // Max amount allowed is the total amount minus the sum of all other split payments
                    const maxAllowedAmount = totalAmount.minus(
                        splitPayments.
                            map(sp => sp.amount).
                            reduce((a, b) => a.plus(b)).
                            minus(sp.amount),
                    );
                    if (bigVal.gt(maxAllowedAmount)) {
                        $splitPaymentAmountInput.value = DollarFormat(maxAllowedAmount);
                        splitPayments[i].amount = maxAllowedAmount;
                    } else {
                        $splitPaymentAmountInput.value = DollarFormat(bigVal);
                        splitPayments[i].amount = bigVal;
                    }
                } else {
                    $splitPaymentAmountInput.value = '';
                    splitPayments[i].amount = Big(0);
                }
                if ($splitPaymentRemainingAmount) {
                    updateSplitPaymentAmountsRemaining();
                }
            });
            if (sp.amount.gt(0)) {
                $splitPaymentAmountInput.value = DollarFormat(sp.amount);
                $splitPaymentAmountInput.dispatchEvent(new Event('input'));
            }
        }
        if ($el.querySelector<HTMLElement>('.split-payment-options-nav-parent')) {
            $el.insertBefore($amountInputClone, $el.querySelector<HTMLElement>('.split-payment-options-nav-parent'));
        } else {
            $el.appendChild($amountInputClone);
        }
    }

    // prompt for a new payment option of a specific type
    function promptNewPaymentOptionByType(typeID: models.PaymentOptionTypeID, $el?: HTMLElement, index?: number, returnTo?: () => void, uploadIDs?: number[]) {
        if (!$el && index !== undefined) {
            $el = $paymentBody?.querySelector<HTMLElement>(`div[data-sp-index="${index}"]`) ?? undefined;
        }
        if (!$el && $paymentBody) {
            $el = $paymentBody;
        }
        if (!index) {
            index = 0;
        }
        if (splitPayments.length > 1) {
            $el?.querySelector<HTMLElement>('.payment-options-nav-new-po')?.remove();
            index = parseInt($el?.getAttribute('data-sp-index') || '0', 10);
        }
        if (!returnTo) {
            returnTo = () => {
                splitPayments[index] = {
                    uuid: UUID(),
                    amount: Big(0),
                    billingShippingSame: true,
                };
                promptNewPaymentOption($el);
            };
        }
        if (!$el) {
            returnTo();
            return;
        }
        $el.classList.remove('new-payment-option-select');
        $el.classList.add('new-payment-option', `new-payment-option-${paymentOptionTypeIDtoName(typeID, true)}`);
        const sp = splitPayments[index];
        sp.paymentOption = {};
        sp.paymentOptionTypeID = typeID;
        switch (typeID) {
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
            $el.replaceChildren(template.Clone('#payment-option-form-new-cc'));
            const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            initCCInputs($el);
            if (sp.billingAddress) {
                const prevAddress = sp.billingAddress.Get();
                const prevSame = sp.billingShippingSame;
                sp.billingAddress = new billingAddressForm($el);
                if (sp.billingAddress.$addrSameToggle) {
                    sp.billingAddress.$addrSameToggle.checked = prevSame || true;
                }
                sp.billingAddress.Set(prevAddress);
            } else {
                sp.billingAddress = new billingAddressForm($el);
            }
            if (sp.billingAddress.$addrSameToggle) {
                sp.billingAddress.$addrSameToggle.addEventListener('click', () => {
                    sp.billingShippingSame = sp.billingAddress?.$addrSameToggle?.checked || false;
                });
            }
            initPaymentTypeSelect(typeID, $el);
            if (returnTo) {
                $el?.querySelector('#payment-option-back')?.addEventListener('click', returnTo);
            } else {
                $el?.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
            }
            const $ccNumber = $el?.querySelector<HTMLInputElement>('.cc-number');
            const $ccCvc = $el?.querySelector<HTMLInputElement>('.cc-cvc');
            const $ccExpiration = $el?.querySelector<HTMLInputElement>('.cc-expiration');
            $ccNumber?.addEventListener('input', () => {
                if (!sp.creditCard) {
                    sp.creditCard = {
                        creditCardNumber: '',
                        expireMonth: 0,
                        expireYear: 0,
                        securityCode: '',
                    };
                }
                sp.creditCard.creditCardNumber = unformatCreditCard($ccNumber.value);
            });
            $ccCvc?.addEventListener('input', () => {
                if (!sp.creditCard) {
                    sp.creditCard = {
                        creditCardNumber: '',
                        expireMonth: 0,
                        expireYear: 0,
                        securityCode: '',
                    };
                }
                sp.creditCard.securityCode = $ccCvc.value;
            });
            $ccExpiration?.addEventListener('input', () => {
                setTimeout(() => {
                    if (!sp.creditCard) {
                        sp.creditCard = {
                            creditCardNumber: '',
                            expireMonth: 0,
                            expireYear: 0,
                            securityCode: '',
                        };
                    }
                    const val = $ccExpiration.value;
                    if (val) {
                        const [monthStr, yearStr] = val.split('/');
                        const month = parseInt(monthStr, 10);
                        const year = parseInt(yearStr, 10);
                        const currentYear = parseInt(new Date().getFullYear().toString().slice(2), 10);
                        if (val.length >= 5) {
                            if ((1 <= month && month <= 12) && (year >= currentYear)) {
                                if (year === currentYear && month < new Date().getMonth() + 1) {
                                    $ccExpiration.setCustomValidity('Invalid expiration date');
                                } else {
                                    $ccExpiration.setCustomValidity('');
                                }
                            } else {
                                $ccExpiration.setCustomValidity('Invalid expiration date');
                            }
                        }
                        ReportValidity($ccExpiration);
                        sp.creditCard.expireMonth = month;
                        sp.creditCard.expireYear = year;
                    }
                });
            });
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK: {
            $el.replaceChildren(template.Clone('#payment-option-form-new-bank'));
            const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            if (sp.billingAddress) {
                const prevAddress = sp.billingAddress.Get();
                const prevSame = sp.billingShippingSame;
                sp.billingAddress = new billingAddressForm($el);
                if (sp.billingAddress.$addrSameToggle) {
                    sp.billingAddress.$addrSameToggle.checked = prevSame || true;
                }
                sp.billingAddress.Set(prevAddress);
            } else {
                sp.billingAddress = new billingAddressForm($el);
            }
            if (sp.billingAddress.$addrSameToggle) {
                sp.billingAddress.$addrSameToggle.addEventListener('click', () => {
                    sp.billingShippingSame = sp.billingAddress?.$addrSameToggle?.checked || false;
                });
            }
            initPaymentTypeSelect(typeID, $el);
            if (returnTo) {
                $el?.querySelector('#payment-option-back')?.addEventListener('click', returnTo);
            } else {
                $el?.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
            }
            const bankAccount = $el?.querySelector<HTMLInputElement>('.bank-acct');
            const bankRouting = $el?.querySelector<HTMLInputElement>('.bank-routing');
            const accountType = $el?.querySelector<HTMLInputElement>('.bank-acct-checking');
            const bankType = $el?.querySelector<HTMLInputElement>('.bank-type-personal');
            bankAccount?.addEventListener('input', () => {
                if (!sp.bank) {
                    sp.bank = {
                        bankAccount: '',
                        bankRouting: '',
                        accountType: 'checking',
                        bankType: 'personal',
                    };
                }
                sp.bank.bankAccount = bankAccount.value;
            });
            bankRouting?.addEventListener('input', () => {
                if (!sp.bank) {
                    sp.bank = {
                        bankAccount: '',
                        bankRouting: '',
                        accountType: 'checking',
                        bankType: 'personal',
                    };
                }
                sp.bank.bankRouting = bankRouting.value;
            });
            accountType?.addEventListener('change', () => {
                if (!sp.bank) {
                    sp.bank = {
                        bankAccount: '',
                        bankRouting: '',
                        accountType: 'checking',
                        bankType: 'personal',
                    };
                }
                sp.bank.accountType = accountType.checked ? 'checking' : 'savings';
            });
            bankType?.addEventListener('change', () => {
                if (!sp.bank) {
                    sp.bank = {
                        bankAccount: '',
                        bankRouting: '',
                        accountType: 'checking',
                        bankType: 'personal',
                    };
                }
                sp.bank.bankType = bankType.checked ? 'personal' : 'business';
            });
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO: {
            $el.replaceChildren(template.Clone('#payment-option-form-new-po'));
            const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            if (sp.billingAddress) {
                const prevAddress = sp.billingAddress.Get();
                const prevSame = sp.billingShippingSame;
                sp.billingAddress = new billingAddressForm($el);
                if (sp.billingAddress.$addrSameToggle) {
                    sp.billingAddress.$addrSameToggle.checked = prevSame || true;
                }
                sp.billingAddress.Set(prevAddress);
            } else {
                sp.billingAddress = new billingAddressForm($el);
            }
            if (sp.billingAddress.$addrSameToggle) {
                sp.billingAddress.$addrSameToggle.addEventListener('click', () => {
                    sp.billingShippingSame = sp.billingAddress?.$addrSameToggle?.checked || false;
                });
            }
            initPaymentTypeSelect(typeID, $el);
            if (returnTo) {
                $el?.querySelector('#payment-option-back')?.addEventListener('click', returnTo);
            } else {
                $el?.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
            }
            const updateIDs = () => {
                if (sp.uploader?.GetUploadIDs().length) {
                    $el.querySelector<HTMLInputElement>('.po-num')?.setCustomValidity('');
                    if (!sp.purchaseOrder) {
                        sp.purchaseOrder = {
                            purchaseOrderNumber: '',
                            purchaseOrderUploadIDs: [],
                        };
                    }
                    sp.purchaseOrder.purchaseOrderUploadIDs = sp.uploader.GetUploadIDs();
                } else {
                    $el.querySelector<HTMLInputElement>('.payment-option-uploader')?.setCustomValidity('Please upload a file');
                }
            };
            // If we want to be able to reload the uploads by ID, we need to keep them. So, if we have any IDs when
            // we create the uploader, we send a request to keep them first.
            let promise = new Promise<void>(resolve => resolve());
            if (uploadIDs) {
                promise = axios.patch('/uploads/keep', { uploadIDs });
            }
            void promise.then(() => {
                sp.uploader = new Uploader($el.querySelector('.uploader-dropzone') as HTMLLabelElement, {
                    confirmBtn: $paymentBody?.querySelector<HTMLButtonElement>('#payment-option-confirm') ?? undefined,
                    uploadIDs: uploadIDs,
                    onidschange: updateIDs,
                    onchange: updateIDs,
                });
                $el?.querySelector<HTMLInputElement>('.po-num')?.addEventListener('input', () => {
                    if (!sp.purchaseOrder) {
                        sp.purchaseOrder = {
                            purchaseOrderNumber: '',
                            purchaseOrderUploadIDs: [],
                        };
                    }
                    sp.purchaseOrder.purchaseOrderNumber = $el.querySelector<HTMLInputElement>('.po-num')?.value || '';
                });
            });
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK: {
            $el.replaceChildren(template.Clone('#payment-option-form-check'));
            const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            if (sp.billingAddress) {
                if (sp.billingAddress) {
                    const prevAddress = sp.billingAddress.Get();
                    const prevSame = sp.billingShippingSame ?? true;
                    sp.billingAddress = new billingAddressForm($el);
                    setTimeout(() => {
                        if (!prevSame) {
                            sp.billingAddress?.$addrSameToggle?.click();
                            sp.billingAddress?.Set(prevAddress);
                        }
                    });
                }
            } else {
                sp.billingAddress = new billingAddressForm($el);
            }
            if (sp.billingAddress.$addrSameToggle) {
                sp.billingAddress.$addrSameToggle.addEventListener('click', () => {
                    sp.billingShippingSame = sp.billingAddress?.$addrSameToggle?.checked || false;
                });
            }
            initPaymentTypeSelect(typeID, $el);
            if (returnTo) {
                $el?.querySelector('#payment-option-back')?.addEventListener('click', returnTo);
            } else {
                $el?.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
            }
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE: {
            $el.replaceChildren(template.Clone('#payment-option-form-wire'));
            const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            if (sp.billingAddress) {
                if (sp.billingAddress) {
                    const prevAddress = sp.billingAddress.Get();
                    const prevSame = sp.billingShippingSame || true;
                    sp.billingAddress = new billingAddressForm($el);
                    setTimeout(() => {
                        if (!prevSame) {
                            sp.billingAddress?.$addrSameToggle?.click();
                            sp.billingAddress?.Set(prevAddress);
                        }
                    });
                }
            } else {
                sp.billingAddress = new billingAddressForm($el);
            }
            if (sp.billingAddress.$addrSameToggle) {
                sp.billingAddress.$addrSameToggle.addEventListener('click', () => {
                    sp.billingShippingSame = sp.billingAddress?.$addrSameToggle?.checked || false;
                });
            }
            initPaymentTypeSelect(typeID, $el);
            if (returnTo) {
                $el?.querySelector('#payment-option-back')?.addEventListener('click', returnTo);
            } else {
                $el?.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
            }
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL: {
            $el.replaceChildren(template.Clone('#payment-option-form-paypal'));
            const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            initPaymentTypeSelect(typeID, $el);
            if (returnTo) {
                $el?.querySelector('#payment-option-back')?.addEventListener('click', returnTo);
            } else {
                $el?.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
            }
            break;
        }
        default:
            returnTo();
            return;
        }

        initSplitPaymentButtons($el, returnTo);

        if (splitPayments.length > 1) {
            initAmountField($el, index);
            updateSplitPaymentAmountsRemaining();
        }

        sp.paymentOptionTypeID = typeID;
        sp.paymentOption = undefined;
        $el?.querySelector<HTMLButtonElement>('#payment-option-confirm')?.addEventListener('click', async () => {
            if (sp.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                if (sp.uploader?.GetUploadIDs().length === 0) {
                    $el?.querySelector<HTMLInputElement>('.payment-option-uploader')?.setCustomValidity('Please upload a file');
                } else {
                    $el?.querySelector<HTMLInputElement>('.payment-option-uploader')?.setCustomValidity('');
                }
            }
            if ($paymentBodyForm) {
                if (ReportValidity($paymentBodyForm)) {
                    if ($paymentBodyFieldSet) {
                        startLoading();
                        $paymentBodyFieldSet.disabled = true;
                    }

                    [
                        $authorizePaymentText,
                        $authorizePaymentTextPlaceOrder,
                        $authorizePaymentTextPayPal,
                    ].forEach($ => $.classList.add('d-none'));

                    let paypal = false;
                    let nonPayment = false;
                    splitPayments.forEach(sp => {
                        remainingAmount = remainingAmount.minus(sp.amount);

                        switch (sp.paymentOptionTypeID) {
                        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
                            paypal = true;
                            break;
                        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
                        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
                        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
                            nonPayment = true;
                            break;
                        }
                    });

                    if (paypal) {
                        $authorizePaymentTextPayPal.classList.remove('d-none');
                    } else if (nonPayment) {
                        $authorizePaymentTextPlaceOrder.classList.remove('d-none');
                    } else {
                        $authorizePaymentText.classList.remove('d-none');
                    }

                    if (sp.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
                        sp.amount = totalAmount;
                        updatePaymentSummary();
                        if ($paymentBodyFieldSet) {
                            $paymentBodyFieldSet.disabled = false;
                        }
                        stopLoading();
                        $paymentBodyToggle?.classList.remove('d-none');
                        $additionalBodyToggle?.click();
                        return;
                    }
                    await createPaymentOption(sp).then(resp => {
                        sp.paymentOption = resp;
                        sp.billingShippingSame = $el?.querySelector<HTMLInputElement>('.billing-shipping-same')?.checked || false;
                        // sp.amount = Big($el.querySelector<HTMLInputElement>('.split-payment-amount-input')?.value ?? 0);
                        sp.amount = totalAmount;
                        paidAmount = Big(0);
                        splitPayments.forEach(sp => {
                            paidAmount.plus(sp.amount);
                        });
                    }).then(updatePaymentSummary).
                        then(() => {
                            $paymentBodyToggle?.classList.remove('d-none');
                            $additionalBodyToggle?.click();
                        }).
                        catch(HandleRequestError).
                        finally(() => {
                            if ($paymentBodyFieldSet) {
                                $paymentBodyFieldSet.disabled = false;
                            }
                            stopLoading();
                        });
                }
            }
        });
    }

    function promptExistingPaymentOption(sp: splitPayment, $el?: HTMLElement, returnTo?: () => void) {
        // Payment option should only be set if it's already been created.
        // If the payment option is in progress or not sent yet, the info
        // should be in the outer fields of the splitPayment object.
        if (sp.paymentOptionTypeID && !sp.paymentOption) {
            let uploadIDs = undefined;
            if (sp.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                uploadIDs = sp.purchaseOrder?.purchaseOrderUploadIDs;
            }
            promptNewPaymentOptionByType(sp.paymentOptionTypeID, $el, undefined, returnTo, uploadIDs);
            switch (sp.paymentOptionTypeID) {
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
                const $ccNumber = $el?.querySelector<HTMLInputElement>('.cc-number');
                const $ccCvc = $el?.querySelector<HTMLInputElement>('.cc-cvc');
                const $ccExp = $el?.querySelector<HTMLInputElement>('.cc-expiration');
                if ($ccNumber && $ccCvc && $ccExp) {
                    $ccNumber.value = sp.creditCard?.creditCardNumber || '';
                    $ccNumber.dispatchEvent(new Event('input'));
                    $ccCvc.value = sp.creditCard?.securityCode || '';
                    $ccCvc.dispatchEvent(new Event('input'));
                    $ccExp.value = `${sp.creditCard?.expireMonth || ''}/${sp.creditCard?.expireYear || ''}`;
                    $ccExp.dispatchEvent(new Event('input'));
                }
                break;
            }
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO: {
                const $poNum = $el?.querySelector<HTMLInputElement>('po-num');
                if ($poNum) {
                    $poNum.value = sp.purchaseOrder?.purchaseOrderNumber || '';
                }
                break;
            }
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK: {
                const $bankAccount = $el?.querySelector<HTMLInputElement>('bank-acct');
                const $bankRouting = $el?.querySelector<HTMLInputElement>('bank-routing');
                const $accountType = $el?.querySelector<HTMLInputElement>('bank-acct-checking');
                const $bankType = $el?.querySelector<HTMLInputElement>('bank-type-personal');
                if ($bankAccount && $bankRouting && $accountType && $bankType) {
                    $bankAccount.value = sp.bank?.bankAccount || '';
                    $bankAccount.dispatchEvent(new Event('input'));
                    $bankRouting.value = sp.bank?.bankRouting || '';
                    $bankRouting.dispatchEvent(new Event('input'));
                    $accountType.value = sp.bank?.accountType || '';
                    $accountType.dispatchEvent(new Event('change'));
                    $bankType.value = sp.bank?.bankType || '';
                    $bankType.dispatchEvent(new Event('change'));
                }
                break;
            }
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
                break;
            }
        }
    }

    function editSavedPaymentOption(poID: string, returnTo?: () => void) {
        if (!returnTo) {
            returnTo = initPayment;
        }
        const upo = Array.from(userPaymentOptions).find(u => u.id === poID);
        if (!upo?.paymentOptionTypeID || !$paymentBody) {
            return;
        }
        switch (upo.paymentOptionTypeID) {
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
            $paymentBody?.replaceChildren(template.Clone('#payment-option-form-edit-cc'));
            const $formBody = $paymentBody.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            initCCInputs($paymentBody);
            break;
        }
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK: {
            $paymentBody?.replaceChildren(template.Clone('#payment-option-form-edit-bank'));
            const $formBody = $paymentBody.querySelector<HTMLDivElement>('.payment-option-form-body');
            if ($formBody) {
                html.IDsToClasses($formBody);
            }
            break;
        }
        default:
            return;
        }
        const billingAddress = new billingAddressForm($paymentBody);
        setTimeout(() => billingAddress.Set(upo));

        $paymentBody?.querySelector<HTMLButtonElement>('#payment-option-back')?.addEventListener('click', () => returnTo());
        $paymentBody?.querySelector<HTMLButtonElement>('#payment-option-confirm')?.addEventListener('click', async () => {
            if ($paymentBodyForm) {
                if (ReportValidity($paymentBodyForm)) {
                    await replacePaymentOption().
                        then(updatePaymentSummary).
                        then(returnTo).
                        catch(HandleRequestError);
                }
            }
        });

        async function replacePaymentOption() {
            if (!upo?.id || !upo?.paymentOptionTypeID) {
                return;
            }
            if ($paymentBodyFieldSet) {
                $paymentBodyFieldSet.disabled = true;
            }
            const $ccNumber = $paymentBody?.querySelector<HTMLInputElement>('.cc-number');
            const $ccCvc = $paymentBody?.querySelector<HTMLInputElement>('.cc-cvc');
            const $ccExp = $paymentBody?.querySelector<HTMLInputElement>('.cc-expiration');
            const $bankAccount = $paymentBody?.querySelector<HTMLInputElement>('.bank-acct');
            const $bankRouting = $paymentBody?.querySelector<HTMLInputElement>('.bank-routing');
            const $accountType = $paymentBody?.querySelector<HTMLInputElement>('.bank-acct-checking');
            const $bankType = $paymentBody?.querySelector<HTMLInputElement>('.bank-type-personal');
            const address = billingAddress?.Get();

            let creditCardNumber: string | undefined;
            let expireMonth: number | undefined;
            let expireYear: number | undefined;
            let securityCode: string | undefined;
            if ($ccNumber && $ccCvc && $ccExp) {
                creditCardNumber = $ccNumber ? unformatCreditCard($ccNumber.value) : undefined;
                const expVal = $ccExp?.value;
                if (expVal) {
                    const [monthStr, yearStr] = expVal.split('/');
                    const month = parseInt(monthStr, 10);
                    const year = parseInt(yearStr, 10);
                    const currentYear = parseInt(new Date().getFullYear().toString().slice(2), 10);
                    if (expVal.length >= 5) {
                        if (1 <= month && month <= 12 && year >= currentYear) {
                            if (year === currentYear && month < new Date().getMonth() + 1) {
                                $ccExp.setCustomValidity('Invalid expiration date');
                            } else {
                                $ccExp.setCustomValidity('');
                            }
                        } else {
                            $ccExp.setCustomValidity('Invalid expiration date');
                        }
                    }
                    ReportValidity($ccExp);
                    expireMonth = month;
                    expireYear = year;
                }
                securityCode = $ccCvc?.value;
            }

            let bankAccount: string | undefined;
            let bankRouting: string | undefined;
            let accountType: 'checking' | 'savings' | undefined;
            let bankType: 'personal' | 'business' | undefined;
            if ($bankAccount && $bankRouting && $accountType && $bankType) {
                bankAccount = $bankAccount?.value;
                bankRouting = $bankRouting?.value;
                accountType = $accountType?.checked ? 'checking' : 'savings';
                bankType = $bankType?.checked ? 'personal' : 'business';
            }

            const paymentOption: models.PaymentOption = {
                paymentOptionTypeID: upo.paymentOptionTypeID,
                fullName: address?.fullName,
                company: address?.company,
                country: address?.country,
                countryID: address?.countryID,
                addressLine1: address?.addressLine1,
                addressLine2: address?.addressLine2,
                city: address?.city,
                state: address?.state,
                stateID: address?.stateID,
                zip: address?.zip,
                email: address?.email,
                phone: address?.phone,
            };
            let deleteFunc = new Promise(resolve => resolve(undefined));
            if (sess?.userID) {
                deleteFunc = DeletePaymentOption(upo?.id);
            }
            await deleteFunc.
                then(async () => NewPaymentOption({
                    user: await userDetails(),
                    fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
                    paymentOption,
                    creditCardNumber,
                    expireMonth,
                    expireYear,
                    securityCode,
                    bankAccount,
                    bankRouting,
                    accountType,
                    bankType,
                })).
                then(resp => {
                    if (!upo?.id || !resp.id) {
                        return;
                    }
                    if ($paymentBodyFieldSet) {
                        $paymentBodyFieldSet.disabled = false;
                    }

                    const undoLinkID = `undo-${UUID()}`;
                    const t = toast.Success(`Updated payment option. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a>`);

                    const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                    undo.addEventListener('click', async e => {
                        e.preventDefault();
                        t?.dispose();
                        if (!upo?.id || !resp.id) {
                            return;
                        }
                        await undoEditSavedPaymentOption(upo.id, resp.id);
                    });
                    return resp;
                }).catch(err => {
                    if ($paymentBodyFieldSet) {
                        $paymentBodyFieldSet.disabled = false;
                    }
                    if (!upo?.id) {
                        return;
                    }
                    void axios.patch(`/payment-options/${upo.id}`, {
                        'active': true,
                    });
                    stopLoading();
                    HandleRequestError(err);
                    throw err;
                });
        }

        async function undoEditSavedPaymentOption(oldID: string, newID: string) {
            await axios.patch(`/payment-options/${oldID}`, {
                'active': true,
            }).then(() =>
                axios.patch(`/payment-options/${newID}`, {
                    'active': false,
                }),
            ).then(initPayment).then(() => {
                const redoLinkID = `redo-${UUID()}`;
                const t = toast.Success(`Reverted payment option. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${redoLinkID}">Undo</a>`);

                const redo = document.querySelector(`#${redoLinkID}`) as HTMLAnchorElement;
                redo.addEventListener('click', async e => {
                    e.preventDefault();
                    t?.dispose();
                    await redoEditSavedPaymentOption(oldID, newID);
                });
            }).catch(HandleRequestError);
        }

        async function redoEditSavedPaymentOption(oldID: string, newID: string) {
            await axios.patch(`/payment-options/${oldID}`, {
                'active': false,
            }).then(() =>
                axios.patch(`/payment-options/${newID}`, {
                    'active': true,
                }),
            ).then(initPayment).then(() => {
                const undoLinkID = `undo-${UUID()}`;
                const t = toast.Success(`Updated payment option. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a>`);

                const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                undo.addEventListener('click', async e => {
                    e.preventDefault();
                    t?.dispose();
                    await undoEditSavedPaymentOption(oldID, newID);
                });
            }).catch(HandleRequestError);
        }
    }

    async function deleteSavedPaymentOption(poID: string) {
        const upo = Array.from(userPaymentOptions).find(u => u.id === poID);
        if (!upo) {
            return;
        }

        if (sess?.userID) {
            await axios.patch(`/payment-options/${poID}`, { 'active': false }).catch(() => undefined);
            const undoLinkID = `undo-${UUID()}`;
            const t = toast.Success(`Deleted payment option. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a>`);

            const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
            undo.addEventListener('click', async e => {
                e.preventDefault();
                t?.dispose();
                await undoDeleteSavedPaymentOption(poID);
            });
        }
        userPaymentOptions.delete(upo);
        splitPayments = splitPayments.filter(sp => sp.paymentOption?.id !== poID);
        await initPayment();

        async function undoDeleteSavedPaymentOption(poID: string) {
            await axios.
                patch(`/payment-options/${poID}`, {
                    'active': true,
                }).
                then(initPayment).
                then(() => {
                    const redoLinkID = `redo-${UUID()}`;
                    const t = toast.Success(`Reverted payment option. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${redoLinkID}">Undo</a>`);

                    const redo = document.querySelector(`#${redoLinkID}`) as HTMLAnchorElement;
                    redo.addEventListener('click', async e => {
                        e.preventDefault();
                        t?.dispose();
                        await redoDeleteSavedPaymentOption(poID);
                    });
                }).
                catch(HandleRequestError);
        }

        async function redoDeleteSavedPaymentOption(poID: string) {
            await axios.
                patch(`/payment-options/${poID}`, {
                    'active': false,
                }).
                then(initPayment).
                then(() => {
                    const undoLinkID = `undo-${UUID()}`;
                    const t = toast.Success(`Updated payment option. <a href="${Relref('/cart')}" class="text-reset text-decoration-underline" id="${undoLinkID}">Undo</a>`);

                    const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
                    undo.addEventListener('click', async e => {
                        e.preventDefault();
                        t?.dispose();
                        await undoDeleteSavedPaymentOption(poID);
                    });
                }).
                catch(HandleRequestError);
        }
    }

    function initPaymentTypeSelect(typeID: models.PaymentOptionTypeID, $el?: HTMLElement) {
        if (!$el && $paymentBody) {
            $el = $paymentBody;
        }
        const $poSelectParent = $el?.querySelector<HTMLDivElement>('#po-type-select') as HTMLDivElement;
        html.IDsToClasses($poSelectParent);
        const $poSelect = $poSelectParent?.querySelector<HTMLSelectElement>('.po-type') as HTMLSelectElement;
        $poSelect.appendChild(html.FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT}">Credit/Debit Card</option>`));
        $poSelect.appendChild(html.FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL}">Paypal</option>`));
        $poSelect.appendChild(html.FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO}">Purchase Order</option>`));
        $poSelect.appendChild(html.FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK}">Check</option>`));
        $poSelect.appendChild(html.FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE}">Wire Transfer</option>`));
        $poSelect.appendChild(html.FromString(`<option value="${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK}">Bank Account</option>`));

        $poSelect.value = typeID.toString();
        $poSelect.addEventListener('change', () => {
            const newType = numberToPaymentOptionTypeID(parseInt($poSelect.value, 10));
            if (newType) {
                promptNewPaymentOptionByType(newType, $el);
            }
        });
    }

    // Init the 'choose your payment option' form
    function promptNewPaymentOption($el?: HTMLElement, returnTo?: () => void) {
        if (!$el && $paymentBody) {
            $el = $paymentBody;
        }
        if (!$el) {
            return;
        }
        $el.replaceChildren(template.Clone('#payment-option-form-new-payment'));
        let className = 'new-payment-option-select';
        if ($el.classList.contains('split-payment')) {
            className = 'new-payment-option-select split-payment';
        }
        $el.className = className;
        let index = 0;
        const $formBody = $el.querySelector<HTMLDivElement>('.payment-option-form-body');
        if ($formBody) {
            html.IDsToClasses($formBody);
        }
        if (splitPayments.length > 1) {
            $el.querySelector<HTMLDivElement>('.form-check:has(input.payment-type-paypal)')?.remove();
            index = parseInt($el.getAttribute('data-sp-index') || '0', 10);
            $el.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach($radio => {
                $radio.name = `payment-type-${index}`;
            });
        } else {
            $paymentBody?.querySelector<HTMLElement>('.payment-options-nav')?.remove();
        }
        if (!returnTo) {
            returnTo = () => {
                splitPayments[index] = {
                    uuid: UUID(),
                    amount: Big(0),
                    billingShippingSame: true,
                };
                void initPayment();
            };
        }

        setTimeout(() => {
            if (!userPaymentOptions.size || !sess?.userID) {
                $el.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
            } else {
                $el.querySelector<HTMLButtonElement>('#payment-option-back')?.addEventListener('click', returnTo);
            }
        });

        if (!$el.querySelector<HTMLElement>('.split-payment-options-nav-parent')) {
            initSplitPaymentButtons($el, returnTo);
        }
        if (splitPayments.length > 1) {
            initAmountField($el, index);
            updateSplitPaymentAmountsRemaining();
        }
    }

    function initCCInputs($el?: HTMLElement) {
        if (!$el && $paymentBody) {
            $el = $paymentBody;
        }
        $el?.querySelectorAll<HTMLInputElement>('.cc-number-input').forEach($cc => {
            registerCursorTracker({ input: $cc, delimiter: DefaultCreditCardDelimiter });
            $cc.addEventListener('input', () => {
                const newVal = formatCreditCard($cc.value, {
                    delimiter: DefaultCreditCardDelimiter,
                });
                $cc.value = newVal;
                let icon = 'generic';
                switch (getCreditCardType($cc.value).toString()) {
                case 'amex':
                    icon = 'amex';
                    break;
                case 'discover':
                    icon = 'discover';
                    break;
                case 'mastercard':
                    icon = 'mastercard';
                    break;
                case 'visa':
                    icon = 'visa';
                    break;
                }
                $el?.querySelectorAll<HTMLElement>('.cc-icon').forEach($icon => {
                    if ($icon.classList.contains(`cc-icon-${icon}`)) {
                        $icon.classList.remove('d-none');
                    } else {
                        $icon.classList.add('d-none');
                    }
                });
            });
        });
        $el?.querySelectorAll<HTMLInputElement>('.cc-exp-input').forEach($exp => {
            registerCursorTracker({ input: $exp, delimiter: DefaultCreditCardDelimiter });
            $exp.addEventListener('input', () => {
                const newVal = formatDate($exp.value, {
                    datePattern: ['m', 'y'],
                });
                $exp.value = newVal;
            });
        });

        $el?.querySelectorAll<HTMLElement>('.cc-cvc-hint').forEach($el => {
            const t = new bootstrap.Tooltip($el, {
                trigger: 'manual',
                placement: 'top',
                title: '3- or 4-digit security code on the back of your card',
                html: true,
                offset: [0, 32],
                customClass: 'cc-cvc-tooltip',
            });
            $el.addEventListener('pointerenter', () => t.show());
            $el.addEventListener('pointerleave', () => t.hide());
        });
    }

    function initSplitPaymentButtons($el: HTMLElement, returnTo?: () => void) {
        if (!$el.getAttribute('data-sp-index')) {
            return;
        }
        const index = parseInt($el.getAttribute('data-sp-index') || '0', 10);
        $el.querySelector<HTMLElement>('.split-payment-options-nav-parent')?.remove();
        if (splitPayments.length > 1) {
            if ($el.classList.contains('new-payment-option-select')) {
                $el.appendChild(template.Clone('#payment-options-nav-new-po-select-template'));
                if (returnTo) {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.addEventListener('click', returnTo);
                } else {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
                }
                $el?.querySelector<HTMLButtonElement>('#payment-option-type-confirm')?.addEventListener('click', () => {
                    const $selected = $el?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
                    if ($selected) {
                        const type = $selected.getAttribute('data-payment-type') || '';
                        let typeID: models.PaymentOptionTypeID | undefined;
                        switch (type) {
                        case 'credit-debit':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
                            break;
                        case 'paypal':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
                            break;
                        case 'purchase-order':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO;
                            break;
                        case 'check':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK;
                            break;
                        case 'wire':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE;
                            break;
                        case 'bank':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK;
                            break;
                        }
                        if (typeID) {
                            promptNewPaymentOptionByType(typeID, $el, index);
                        }
                    }
                });
            } else if ($el.classList.contains('new-payment-option')) {
                $el.appendChild(template.Clone('#payment-options-split-back'));
                if (returnTo) {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.addEventListener('click', returnTo);
                } else {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
                }
            } else {
                $el.appendChild(template.Clone('#payment-options-split-controls'));
                $el.querySelector<HTMLButtonElement>('#payment-new')?.addEventListener('click', () => promptNewPaymentOption($el, () => {
                    splitPayments[index] = {
                        uuid: UUID(),
                        amount: Big(0),
                        billingShippingSame: true,
                    };
                    void initPayment();
                }));
            }
        } else {
            // Prompting for new payment option, only one payment
            if ($el.classList.contains('new-payment-option')) {
                $el.appendChild(template.Clone('#payment-options-nav-new-po-template'));
                if (returnTo) {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.addEventListener('click', returnTo);
                } else {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
                }

                if (splitPayments[index].paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
                    $el.querySelector<HTMLButtonElement>('#payment-split')?.remove();
                } else {
                    // $el.querySelector<HTMLButtonElement>('#payment-split')?.addEventListener('click', () => {
                    //     if (splitPayments.length <= 1) {
                    //         splitPayments[0].amount = Big(0);
                    //     }
                    //     splitPayments.push({
                    //         uuid: UUID(),
                    //         amount: Big(0),
                    //         billingShippingSame: true,
                    //     });
                    //     void initPayment();
                    // });
                }

                $el.querySelector<HTMLButtonElement>('#payment-confirm')?.addEventListener('click', async () => {
                    if ($paymentBodyForm && !ReportValidity($paymentBodyForm)) {
                        return;
                    }
                    if ($paymentBodyFieldSet) {
                        $paymentBodyFieldSet.disabled = true;
                        startLoading();
                    }
                    splitPayments[0].amount = totalAmount;
                    await updateTotals().then(async () => {
                        if (remainingAmount.gt(0)) {
                            return;
                        }
                        $paymentBody?.querySelectorAll<HTMLDivElement>('.split-payment').forEach($sp => {
                            let amount = remainingAmount;
                            if (splitPayments.length > 1) {
                                const input = $sp.querySelector<HTMLInputElement>('.split-payment-amount-input');
                                if (input) {
                                    const val = input.value.replace(/[^0-9.]/g, '');
                                    if (val) {
                                        amount = Big(val);
                                    }
                                    const index = parseInt($sp.getAttribute('data-sp-index') || '0', 10);
                                    splitPayments[index].amount = amount;
                                }
                            }
                        });
                        await updateOrderSummary().
                            then(updatePaymentSummary).
                            then(() => {
                                if (remainingAmount.gt(0)) {
                                    $paymentBody?.querySelectorAll<HTMLInputElement>('.split-payment-amount-input').forEach($input => $input.classList.add('is-invalid'));
                                    return;
                                } else {
                                    $paymentBody?.querySelectorAll<HTMLInputElement>('.split-payment-amount-input').forEach($input => $input.classList.remove('is-invalid'));
                                    $paymentBodyToggle?.classList.remove('d-none');
                                    initAdditional();
                                    $additionalBodyToggle?.click();
                                }
                            }).
                            catch(HandleRequestError).
                            finally(() => {
                                if ($paymentBodyFieldSet) {
                                    $paymentBodyFieldSet.disabled = false;
                                }
                                stopLoading();
                            });
                    });
                });
            } else if ($el.classList.contains('new-payment-option-select')) {
                $el.appendChild(template.Clone('#payment-options-nav-new-po-select-template'));
                if (returnTo) {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.addEventListener('click', returnTo);
                } else {
                    $el.querySelector<HTMLButtonElement>('#payment-option-back')?.classList.add('d-none');
                }
                $el.querySelector<HTMLButtonElement>('#payment-option-type-confirm')?.addEventListener('click', () => {
                    const $selected = $el?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
                    if ($selected) {
                        splitPayments[0].paymentOptionTypeID = numberToPaymentOptionTypeID(parseInt($selected.value, 10));
                        splitPayments[0].paymentOption = undefined;
                        const type = $selected.getAttribute('data-payment-type') || '';
                        let typeID: models.PaymentOptionTypeID | undefined;
                        switch (type) {
                        case 'credit-debit':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
                            break;
                        case 'paypal':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
                            break;
                        case 'purchase-order':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO;
                            break;
                        case 'check':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK;
                            break;
                        case 'wire':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE;
                            break;
                        case 'bank':
                            typeID = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK;
                            break;
                        }
                        if (typeID) {
                            promptNewPaymentOptionByType(typeID, $el, index);
                        }
                        if (typeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
                            $el?.querySelector<HTMLButtonElement>('#payment-option-confirm')?.dispatchEvent(new Event('click'));
                        }
                    }
                });
            } else {
                $paymentBody?.querySelector<HTMLButtonElement>('#payment-option-confirm')?.addEventListener('click', () => {
                    if ($paymentBodyForm && !ReportValidity($paymentBodyForm)) {
                        return;
                    }
                    $el.querySelectorAll<HTMLInputElement>('input[type=radio]').forEach($input => $input.dispatchEvent(new Event('clearinvalid')));
                    const $selected = $el?.querySelector<HTMLInputElement>('.payment-option input[type="radio"]:checked');
                    const id = $selected?.value;
                    if (id) {
                        if ($paymentBodyFieldSet) {
                            $paymentBodyFieldSet.disabled = true;
                        }
                        startLoading();
                        const upo = Array.from(userPaymentOptions).find(u => u.id === id);
                        if (upo) {
                            let index = 0;
                            if (splitPayments.length > 1) {
                                index = parseInt($el.parentElement?.getAttribute('data-sp-index') || '0', 10);
                            }
                            splitPayments[index].paymentOption = upo;
                        }
                        if ($paymentBodyFieldSet) {
                            $paymentBodyFieldSet.disabled = false;
                        }
                        stopLoading();
                        $paymentBodyToggle?.classList.remove('d-none');
                        $additionalBodyToggle?.click();
                    } else {
                        return;
                    }
                });
            }
        }
    }

    async function createPaymentOption(sp: splitPayment): Promise<models.PaymentOption> {
        const address = sp.billingAddress?.Get();
        const paymentOption: models.PaymentOption = {
            paymentOptionTypeID: sp.paymentOptionTypeID,
            fullName: address?.fullName,
            company: address?.company,
            country: address?.country,
            countryID: address?.countryID,
            addressLine1: address?.addressLine1,
            addressLine2: address?.addressLine2,
            city: address?.city,
            state: address?.state,
            stateID: address?.stateID,
            zip: address?.zip,
            email: address?.email,
            phone: address?.phone,
        };
        return NewPaymentOption({
            user: await userDetails(),
            fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
            paymentOption,
            creditCardNumber: sp.creditCard?.creditCardNumber,
            expireMonth: sp.creditCard?.expireMonth,
            expireYear: sp.creditCard?.expireYear,
            securityCode: sp.creditCard?.securityCode,
            bankAccount: sp.bank?.bankAccount,
            bankRouting: sp.bank?.bankRouting,
            accountType: sp.bank?.accountType,
            bankType: sp.bank?.bankType,
        }).then(resp => sp.paymentOption = resp);
    }

    function updatePaymentSummary() {
        if (splitPayments.length && splitPayments.filter(sp => sp.paymentOption?.id || sp.paymentOptionTypeID).length) {
            $paymentPreview.classList.value = 'preview px-md-17 pt-2 pb-10';
            $paymentPreview?.replaceChildren(template.Clone($paymentPreviewTemplate));
            const $paymentPreviewInnerParent = $paymentPreview?.querySelector('.payment-preview-inner-parent') as HTMLElement;
            splitPayments.forEach((sp, i) => {
                if (sp.paymentOption) {
                    if (sp.paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
                        const $previewItem = template.Clone($paymentPreviewItemBank);
                        const $index = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-index') as HTMLSpanElement;
                        const $lastFour = $previewItem.querySelector<HTMLSpanElement>('.bank-last-four') as HTMLSpanElement;
                        const $acctType = $previewItem.querySelector<HTMLSpanElement>('.acct-type') as HTMLSpanElement;
                        const $bankType = $previewItem.querySelector<HTMLSpanElement>('.bank-type') as HTMLSpanElement;
                        const $amount = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-amount') as HTMLSpanElement;
                        $index.textContent = (i + 1).toString();
                        $lastFour.textContent = sp.paymentOption.creditCardLast4?.toString() || '';
                        $acctType.textContent = capitalizeFirstLetter(sp.paymentOption.accountType || '');
                        $bankType.textContent = capitalizeFirstLetter(sp.paymentOption.bankType || '');
                        $amount.textContent = DollarFormat(sp.amount);
                        $paymentPreviewInnerParent.appendChild($previewItem);
                    } else if (sp.paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
                        const $previewItem = template.Clone($paymentPreviewItemCC);
                        const $index = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-index') as HTMLSpanElement;
                        const $lastFour = $previewItem.querySelector<HTMLSpanElement>('.cc-last-four') as HTMLSpanElement;
                        const $issuer = $previewItem.querySelector<HTMLSpanElement>('.cc-issuer') as HTMLSpanElement;
                        const $amount = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-amount') as HTMLSpanElement;
                        $index.textContent = (i + 1).toString();
                        $lastFour.textContent = sp.paymentOption.creditCardLast4?.toString().padStart(4, '0') || '';
                        switch (getCreditCardType(sp.paymentOption.creditCardFirst6?.toString() ?? '').toString()) {
                        case 'amex':
                            $issuer.textContent = 'Amex';
                            $previewItem.querySelector('.cc-icon-generic')?.classList.add('d-none');
                            $previewItem.querySelector('.cc-icon-amex')?.classList.remove('d-none');
                            break;
                        case 'discover':
                            $issuer.textContent = 'Discover';
                            $previewItem.querySelector('.cc-icon-generic')?.classList.add('d-none');
                            $previewItem.querySelector('.cc-icon-discover')?.classList.remove('d-none');
                            break;
                        case 'mastercard':
                            $issuer.textContent = 'Mastercard';
                            $previewItem.querySelector('.cc-icon-generic')?.classList.add('d-none');
                            $previewItem.querySelector('.cc-icon-mastercard')?.classList.remove('d-none');
                            break;
                        case 'visa':
                            $issuer.textContent = 'Visa';
                            $previewItem.querySelector('.cc-icon-generic')?.classList.add('d-none');
                            $previewItem.querySelector('.cc-icon-visa')?.classList.remove('d-none');
                            break;
                        }
                        $amount.textContent = DollarFormat(sp.amount);
                        $paymentPreviewInnerParent.appendChild($previewItem);
                    } else {
                        const $previewItem = template.Clone($paymentPreviewItemOther);
                        const $poType = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-type') as HTMLSpanElement;
                        const $index = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-index') as HTMLSpanElement;
                        const $amount = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-amount') as HTMLSpanElement;
                        $poType.textContent = capitalizeFirstLetter(paymentOptionTypeIDtoName(sp.paymentOptionTypeID ?? sp.paymentOption?.paymentOptionTypeID));
                        $index.textContent = (i + 1).toString();
                        $amount.textContent = DollarFormat(sp.amount);
                        $paymentPreviewInnerParent.appendChild($previewItem);
                    }
                } else if (sp.paymentOptionTypeID) {
                    const $previewItem = template.Clone($paymentPreviewItemOther);
                    const $poType = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-type') as HTMLSpanElement;
                    const $index = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-index') as HTMLSpanElement;
                    const $amount = $previewItem.querySelector<HTMLSpanElement>('.payment-preview-item-amount') as HTMLSpanElement;
                    $previewItem.querySelector<HTMLDivElement>('.in-progress')?.classList.remove('d-none');
                    $poType.textContent = paymentOptionTypeIDtoName(sp.paymentOptionTypeID);
                    $index.textContent = (i + 1).toString();
                    $amount.textContent = DollarFormat(sp.amount);
                    $paymentPreviewInnerParent.appendChild($previewItem);
                }
            });
        } else {
            $paymentPreview.replaceChildren(html.FromString('<div class="w-100 py-16 px-md-20 text-body-secondary text-center fs-4 fst-italic">No payment option selected</div>'));
        }
    }

    function initAdditional() {
        $additionalBodyToggle?.classList.add('d-none');
    }

    const $additionalDetailsForm = document.querySelector<HTMLFormElement>('#additional-details') as HTMLFormElement;

    const $eventDateYes = $additionalBody?.querySelector<HTMLInputElement>('#event-date-yes') as HTMLInputElement;
    const $eventDateNo = $additionalBody?.querySelector<HTMLInputElement>('#event-date-no') as HTMLInputElement;
    const $eventDate = $additionalBody?.querySelector<HTMLInputElement>('#event-date') as HTMLInputElement;

    const $promoApproval = $additionalBody?.querySelector<HTMLInputElement>('#promo-approval') as HTMLInputElement;
    const $smsApproval = $additionalBody?.querySelector<HTMLInputElement>('#sms-approval') as HTMLInputElement;

    const $designApproval = $additionalBody?.querySelector<HTMLInputElement>('#design-approval') as HTMLInputElement;

    [$eventDateYes, $eventDateNo].forEach($toggle => {
        $toggle.addEventListener('change', () => {
            if ($eventDateYes.checked) {
                $eventDate.parentElement?.classList.remove('d-none');
                $eventDate.required = true;
                additionalDetails.eventDate = DateTime.fromISO($eventDate.value);
            } else {
                $eventDate.parentElement?.classList.add('d-none');
                $eventDate.required = false;
                $eventDate.value = '';
                additionalDetails.eventDate = undefined;
            }
        });
    });

    $promoApproval.addEventListener('change', () => additionalDetails.marketingPermission = $promoApproval.checked);
    $smsApproval.addEventListener('change', () => additionalDetails.smsOptIn = $smsApproval.checked);
    $designApproval.addEventListener('change', () => additionalDetails.verifiedDesigns = $designApproval.checked);

    $additionalBody?.querySelector('#additional-details-confirm')?.addEventListener('click', async () => {
        if ($eventDateYes.checked) {
            if ($eventDate.value) {
                if (DateTime.fromISO($eventDate.value) < DateTime.now()) {
                    $eventDate.setCustomValidity('Event date cannot be in the past');
                } else {
                    $eventDate.setCustomValidity('');
                }
            }
        }
        if (ReportValidity($additionalDetailsForm)) {
            additionalDetails = {
                eventDate: $eventDate.value ? DateTime.fromISO($eventDate.value) : undefined,
                marketingPermission: $promoApproval.checked,
                smsOptIn: $smsApproval.checked,
                verifiedDesigns: $designApproval.checked,
            };
            await updateOrderSummary().
                then(updateShippingSummary).
                then(updateTaxExemptionSummary).
                then(updateAdditionalDetailsSummary).
                then(() => {
                    $additionalBodyToggle?.click();
                    if (shippingOption && splitPayments.length > 0) {
                        $authorizePaymentBtn.disabled = false;
                    } else {
                        $authorizePaymentBtn.disabled = true;
                    }
                }).catch(HandleRequestError);
        }
        $additionalBodyToggle?.classList.remove('d-none');
    });

    function updateAdditionalDetailsSummary() {
        // TO-DO
        $additionalPreview?.replaceChildren(template.Clone('#additional-details-summary'));
        const $previewEventDate = $additionalPreview?.querySelector('#additional-details-preview-section-event-date') as HTMLDivElement;
        const $previewPermissions = $additionalPreview?.querySelector('#additional-details-preview-section-permissions') as HTMLDivElement;
        const $previewVerification = $additionalPreview?.querySelector('#additional-details-preview-section-verification') as HTMLDivElement;
        if ($previewEventDate) {
            const $eventDateBody = template.Clone('#additional-details-summary-item');
            const $responseShortParent = $eventDateBody.querySelector('.response-short') as HTMLSpanElement;
            const $symbol = $responseShortParent.querySelector('.material-symbols-rounded') as HTMLSpanElement;
            const $responseShortText = $responseShortParent.querySelector('.response-short-text') as HTMLSpanElement;
            const $mainText = $eventDateBody.querySelector('.main-text') as HTMLSpanElement;
            if (additionalDetails?.eventDate) {
                $responseShortParent.classList.remove('text-red');
                $responseShortParent.classList.add('text-green-800');
                $symbol.textContent = 'check_circle';
                $responseShortText.textContent = 'YES';
                $mainText.textContent = `I have an event on ${additionalDetails.eventDate.toLocaleString()}`;
            } else {
                $responseShortParent.classList.remove('text-green-800');
                $responseShortParent.classList.add('text-red');
                $symbol.textContent = 'cancel';
                $responseShortText.textContent = 'NO';
                $mainText.textContent = 'I do not have an event date';
            }
            $previewEventDate.appendChild($eventDateBody);
        }
        if ($previewPermissions) {
            const $permissionMarketingBody = template.Clone('#additional-details-summary-item');
            const $responseShortParentMarketing = $permissionMarketingBody.querySelector('.response-short') as HTMLSpanElement;
            const $symbolMarketing = $responseShortParentMarketing.querySelector('.material-symbols-rounded') as HTMLSpanElement;
            const $responseShortTextMarketing = $responseShortParentMarketing.querySelector('.response-short-text') as HTMLSpanElement;
            const $mainTextMarketing = $permissionMarketingBody.querySelector('.main-text') as HTMLSpanElement;
            if (additionalDetails?.marketingPermission) {
                $responseShortParentMarketing.classList.remove('text-red');
                $responseShortParentMarketing.classList.add('text-green-800');
                $symbolMarketing.textContent = 'check_circle';
                $responseShortTextMarketing.textContent = 'YES';
                $mainTextMarketing.textContent = 'I give permission to allow my designs to be used for marketing';
            } else {
                $responseShortParentMarketing.classList.remove('text-green-800');
                $responseShortParentMarketing.classList.add('text-red');
                $symbolMarketing.textContent = 'cancel';
                $responseShortTextMarketing.textContent = 'NO';
                $mainTextMarketing.textContent = 'I do not give permission to allow my designs to be used for marketing';
            }
            $previewPermissions.appendChild($permissionMarketingBody);
            const $permissionSMSBody = template.Clone('#additional-details-summary-item');
            const $responseShortParentSMS = $permissionSMSBody.querySelector('.response-short') as HTMLSpanElement;
            const $symbolSMS = $responseShortParentSMS.querySelector('.material-symbols-rounded') as HTMLSpanElement;
            const $responseShortTextSMS = $responseShortParentSMS.querySelector('.response-short-text') as HTMLSpanElement;
            const $mainTextSMS = $permissionSMSBody.querySelector('.main-text') as HTMLSpanElement;
            if (additionalDetails?.smsOptIn) {
                $responseShortParentSMS.classList.remove('text-red');
                $responseShortParentSMS.classList.add('text-green-800');
                $symbolSMS.textContent = 'check_circle';
                $responseShortTextSMS.textContent = 'YES';
                $mainTextSMS.textContent = 'I would like to receive SMS updates';
            } else {
                $responseShortParentSMS.classList.remove('text-green-800');
                $responseShortParentSMS.classList.add('text-red');
                $symbolSMS.textContent = 'cancel';
                $responseShortTextSMS.textContent = 'NO';
                $mainTextSMS.textContent = 'I would not like to receive SMS updates';
            }
            $previewPermissions.appendChild($permissionSMSBody);
        }
        if ($previewVerification) {
            const $verificationBody = template.Clone('#additional-details-summary-item');
            const $responseShortParent = $verificationBody.querySelector('.response-short') as HTMLSpanElement;
            const $symbol = $responseShortParent.querySelector('.material-symbols-rounded') as HTMLSpanElement;
            const $responseShortText = $responseShortParent.querySelector('.response-short-text') as HTMLSpanElement;
            const $mainText = $verificationBody.querySelector('.main-text') as HTMLSpanElement;
            if (additionalDetails?.verifiedDesigns) {
                $responseShortParent.classList.remove('text-red');
                $responseShortParent.classList.add('text-green-800');
                $symbol.textContent = 'check_circle';
                $responseShortText.textContent = 'YES';
                $mainText.textContent = 'I have verified that all colors, content, and spelling are exactly correct';
            } else {
                $responseShortParent.classList.remove('text-green-800');
                $responseShortParent.classList.add('text-red');
                $symbol.textContent = 'cancel';
                $responseShortText.textContent = 'NO';
                $mainText.textContent = 'I have not verified that all colors, content, and spelling are exactly correct';
            }
            $previewVerification.appendChild($verificationBody);
        }
    }

    $additionalBodyToggle?.addEventListener('click', () => {
        // setTimeout gives the bootstrap js time to update the class
        setTimeout(() => {
            if (!$additionalBodyToggle?.classList.contains('collapsed')) {
                initAdditional();
                // wait for the collapse transition to finish before scrolling
                setTimeout(() => {
                    $additionalBody?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }, 350);
            }
        });
    });

    $authorizePaymentBtn.addEventListener('click', submitOrder);

    let orderInProgress = false;
    async function submitOrder() {
        if (orderInProgress) {
            return;
        }

        let isPayPal = false;
        for (const sp of splitPayments) {
            if (sp.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL) {
                isPayPal = true;
                break;
            }
        }

        if (!shippingOption?.id || (!isPayPal && !splitPayments[0].paymentOption?.id)) {
            return;
        }
        startLoading();
        orderInProgress = true;
        $authorizePaymentBtn.disabled = true;
        document.querySelectorAll('.accordion-header a[data-bs-toggle="collapse"]').forEach($btn => {
            ($btn as HTMLAnchorElement).classList.add('d-none');
        });

        let orderFunc = Promise.resolve(orderResp);
        if (orderResp === undefined) {
            orderFunc = axios.post<createOrderResp>('/orders', {
                'user': await userDetails(),
                'cartID': cartID ?? (await carts.GetCart())?.cart.id,
                'shippingOptionIDs': [shippingOption?.id],
                'userTaxExemptionID': taxExemption?.id,
                'eventDate': additionalDetails.eventDate?.toISODate(),
                'sms': additionalDetails.smsOptIn,
                'promotionalApproval': additionalDetails.marketingPermission,
                'promo': promo?.code,
            }).then(resp => {
                orderResp = resp.data;
                if (!orderResp.orderID) {
                    HandleRequestError('Order not found');
                }
                return resp.data;
            }).catch(e => {
                HandleRequestError(e);
                $authorizePaymentBtn.disabled = false;
                stopLoading();
                throw e;
            });
        }

        let createOrderPayments;

        if (!isPayPal) {
            let createOrderPaymentsInFlight = false;
            createOrderPayments = async () => {
                if (createOrderPaymentsInFlight) {
                    return;
                }
                createOrderPaymentsInFlight = true;

                if (!orderResp) {
                    HandleRequestError('Order not found');
                }
                const sp = splitPayments[0];
                const orderPaymentReq: Record<string, unknown> = {
                    'user': await userDetails(),
                    'orderIDs': [orderResp?.orderID],
                    'paymentOptionID': sp.paymentOption?.key ?? sp.paymentOption?.id,
                    'amount': sp.amount.toNumber(),
                };

                if (sp.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                    orderPaymentReq['poNumber'] = sp.purchaseOrder?.purchaseOrderNumber;
                    orderPaymentReq['poUploadIDs'] = sp.purchaseOrder?.purchaseOrderUploadIDs;
                }
                if (!sess?.userID) {
                    orderPaymentReq['user'] = await userDetails();
                }
                await axios.post<models.OrderPayment[]>('/order-payments', orderPaymentReq).then(() => {
                    sp.success = true;
                    sp.msg = 'Payment successful';

                    createOrderPayments = undefined;
                }).catch(resp => {
                    const $paymentSectionBtn = $sectionPayment?.querySelector('.accordion-header a[data-bs-toggle="collapse"]') as HTMLAnchorElement;
                    $paymentSectionBtn.classList.remove('d-none');
                    $authorizePaymentBtn.disabled = false;
                    $paymentSectionBtn.click();
                    const failedCount = splitPayments.filter(sp => sp.success === false).length;
                    toast.Error(`${failedCount} payment${failedCount > 1 ? 's' : ''} failed.`);
                    $authorizePaymentBtn.innerText = `Retry Payment${failedCount > 1 ? 's' : ''}`;

                    sp.success = false;
                    sp.msg = 'Payment failed';
                    if (IsAxiosError(resp) && IsParamErrors(resp.response?.data)) {
                        const e = resp.response?.data.errors;
                        if (e?.length) {
                            sp.msg = e[0].message;
                        }
                    }
                    HandleRequestError(sp.msg);

                    throw new Error(sp.msg);
                }).finally(() => {
                    createOrderPaymentsInFlight = false;
                });
            };
        }

        await orderFunc.
            then(createOrderPayments).
            then(async () => {
                if (isPayPal) {
                    const payPalRedirectURL = await paypal.CreateOrder({
                        userDetails: await userDetails(),
                        orderIDs: [orderResp?.orderID ?? ''],
                        successURL: `${window.location.protocol}//${window.location.host}${Relref('/order-success')}?id=${orderResp?.key ?? ''}&deleteCart=true`,
                        cancelURL: `${window.location.protocol}//${window.location.host}${Relref('/checkout')}`,
                    });

                    window.location.href = payPalRedirectURL || '';
                } else {
                    await carts.DeleteCart();

                    await axios.post(`/orders/${orderResp?.orderID ?? ''}/workflows/placed`).catch(() => undefined);
                    window.location = `${Relref('/order-success')}?id=${orderResp?.key ?? ''}`;
                }
            }).
            finally(() => orderInProgress = false);
    }
    class billingAddressForm {
        $fullName: HTMLInputElement;
        $company: HTMLInputElement;
        $country: HTMLSelectElement;
        $addressLine1: HTMLInputElement;
        $addressLine2: HTMLInputElement;
        $city: HTMLInputElement;
        $state: HTMLSelectElement;
        $zipCode: HTMLInputElement;
        $email: HTMLInputElement;
        $phone: HTMLInputElement;

        $billingFields: HTMLDivElement;
        $addrSameToggle?: HTMLInputElement;

        private billingCollapse?: bootstrap.Collapse;
        private phoneIti: Phone;

        FullName: string = '';
        Company: string = '';
        Country: models.Country | undefined;
        CountryID: models.CountryID | undefined;
        AddressLine1: string = '';
        AddressLine2: string = '';
        City: string = '';
        State: models.State | undefined;
        StateID: models.StateID | undefined;
        Zip: string = '';
        Email: string = '';
        Phone: string = '';

        ShowOrHide = () => {
            if (!this.$addrSameToggle) {
                return;
            }
            if (this.$addrSameToggle?.checked) {
                this.billingCollapse?.hide();
            } else {
                this.billingCollapse?.show();
            }
        };

        Get = (): models.Address => {
            if (this.$addrSameToggle?.checked && shippingOption) {
                this.FullName = shippingOption?.fullName ?? '';
                this.Company = shippingOption?.company ?? '';
                this.AddressLine1 = shippingOption?.addressLine1 ?? '';
                this.AddressLine2 = shippingOption?.addressLine2 ?? '';
                this.City = shippingOption?.city ?? '';
                this.Zip = shippingOption?.zip ?? '';
                this.Email = shippingOption?.email ?? '';

                this.Phone = shippingOption?.phone ?? '';

                this.Country = shippingOption.country;
                this.CountryID = shippingOption.country?.id ?? shippingOption.countryID;

                this.State = shippingOption.state;
                this.StateID = shippingOption.state?.id ?? shippingOption.stateID;
            }
            return {
                fullName: this.FullName,
                company: this.Company,
                addressLine1: this.AddressLine1,
                addressLine2: this.AddressLine2,
                city: this.City,
                zip: this.Zip,
                email: this.Email,
                phone: this.Phone,
                countryID: this.CountryID,
                country: this.Country,
                stateID: this.StateID,
                state: this.State,
            };
        };

        Set = (addr: models.Address) => {
            this.FullName = addr.fullName || '';
            this.Company = addr.company || '';
            this.AddressLine1 = addr.addressLine1 || '';
            this.AddressLine2 = addr.addressLine2 || '';
            this.City = addr.city || '';
            this.Zip = addr.zip || '';
            this.Email = addr.email || '';
            this.Phone = addr.phone || '';

            this.Country = addr.country;
            this.CountryID = addr.country?.id || addr.countryID;

            this.State = addr.state;
            this.StateID = addr.state?.id || addr.stateID;

            this.$fullName.value = this.FullName;
            this.$company.value = this.Company;
            this.$addressLine1.value = this.AddressLine1;
            this.$addressLine2.value = this.AddressLine2;
            this.$city.value = this.City;
            this.$zipCode.value = this.Zip;
            this.$email.value = this.Email;
            // this.$phone.value = this.Phone;

            if (this.CountryID) {
                this.$country.value = this.CountryID.toString();
                this.$country.dispatchEvent(new Event('change'));
            }
            if (this.StateID) {
                this.$state.value = this.StateID.toString();
            }

            setTimeout(async () => {
                await this.phoneIti.Iti.promise.then(() => {
                    this.phoneIti.Iti.setNumber(this.Phone);
                });
            });
        };

        constructor($el?: HTMLElement) {
            if (!$el && $paymentBody) {
                $el = $paymentBody;
            }
            this.$billingFields = $el?.querySelector<HTMLDivElement>('.billing-address-form') as HTMLDivElement;
            this.$addrSameToggle = $el?.querySelector<HTMLInputElement>('.billing-shipping-same') ?? undefined;
            if (this.$addrSameToggle) {
                this.billingCollapse = new bootstrap.Collapse(this.$billingFields as HTMLElement, {
                    toggle: false,
                });
                this.ShowOrHide();
                this.$addrSameToggle?.addEventListener('click', this.ShowOrHide);
            }

            this.$fullName = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-full-name') as HTMLInputElement;
            this.$company = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-company') as HTMLInputElement;
            this.$country = this.$billingFields?.querySelector<HTMLSelectElement>('.new-billing-address-country') as HTMLSelectElement;
            this.$addressLine1 = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-line-1') as HTMLInputElement;
            this.$addressLine2 = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-line-2') as HTMLInputElement;
            this.$city = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-city') as HTMLInputElement;
            this.$state = this.$billingFields?.querySelector<HTMLSelectElement>('.new-billing-address-state') as HTMLSelectElement;
            this.$zipCode = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-zip') as HTMLInputElement;
            this.$email = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-email') as HTMLInputElement;
            this.$phone = this.$billingFields?.querySelector<HTMLInputElement>('.new-billing-address-phone') as HTMLInputElement;

            this.phoneIti = new Phone(this.$phone);

            countries.Selects(this.$country, this.$state, {
                defaultUnitedStates: true,
                stateParentSelector: 'div',
                phone: this.phoneIti,
                onStateHide: () => this.$zipCode.parentElement?.classList.replace('col-3', 'col-6'),
                onStateShow: () => this.$zipCode.parentElement?.classList.replace('col-6', 'col-3'),
            }).then(() => {
                if (shippingOption?.countryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
                    this.$country.value = shippingOption?.country?.id?.toString() || '';
                    this.$country.dispatchEvent(new Event('change'));
                }
            }).then(() => {
                if (shippingOption?.stateID) {
                    this.$state.value = shippingOption.stateID.toString();
                }
            }).catch(HandleRequestError);

            if (shippingOption) {
                this.Set(shippingOption);
            }
            this.$fullName.addEventListener('input', () => this.FullName = this.$fullName.value);
            this.$company.addEventListener('input', () => this.Company = this.$company.value);
            this.$addressLine1.addEventListener('input', () => this.AddressLine1 = this.$addressLine1.value);
            this.$addressLine2.addEventListener('input', () => this.AddressLine2 = this.$addressLine2.value);
            this.$city.addEventListener('input', () => this.City = this.$city.value);
            this.$zipCode.addEventListener('input', () => this.Zip = this.$zipCode.value);
            this.$email.addEventListener('input', () => this.Email = this.$email.value);

            this.$phone.addEventListener('input', () => this.Phone = this.phoneIti.Iti.getNumber());

            this.$country.addEventListener('change', async () => {
                this.Country = await countries.GetCountry(parseInt(this.$country.value, 10));
                this.CountryID = this.Country?.id;

                this.State = await countries.GetState(parseInt(this.$country.value, 10), parseInt(this.$state.value, 10));
                this.StateID = this.State?.id;
            });

            this.$state.addEventListener('change', async () => {
                this.State = await countries.GetState(parseInt(this.$country.value, 10), parseInt(this.$state.value, 10));
                this.StateID = this.State?.id;
            });
        }
    }
}

function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function numberToPaymentOptionTypeID(num: number): models.PaymentOptionTypeID | undefined {
    if (Object.values(models.PaymentOptionTypeID).includes(num as models.PaymentOptionTypeID)) {
        return num as models.PaymentOptionTypeID;
    }
    return undefined;
}

function paymentOptionTypeIDtoName(id?: models.PaymentOptionTypeID, short: boolean = false): string {
    if (short) {
        switch (id) {
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
            return 'cc';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
            return 'paypal';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
            return 'po';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
            return 'check';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
            return 'wire';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
            return 'bank';
        default:
            return 'unknown';
        }
    } else {
        switch (id) {
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
            return 'Credit/Debit Card';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL:
            return 'PayPal';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO:
            return 'Purchase Order';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK:
            return 'Check';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE:
            return 'Wire Transfer';
        case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
            return 'Bank Account';
        default:
            return 'unknown';
        }
    }
}
